/**
 * Les ordres de transport, vus par l'écran — le calcul, hors de React.
 *
 * Même découpe que `parc.ts` et `affectation.ts`, et pour la même raison : ce
 * qui est ici se teste sans navigateur, sans base et sans session.
 *
 * TROIS CHOSES QUE CE FICHIER SAIT, ET QUE L'ÉCRAN N'A PAS À REFAIRE :
 *
 *   - **un ordre n'a ni date ni trajet à lui.** Les deux viennent des étapes, et
 *     l'API les rend déjà calculés (`jour_debut`, `ville_depart`…). Ici on les
 *     met en français ;
 *   - **les totaux de marchandise se somment sur les CHARGEMENTS seuls.**
 *     Compter aussi les livraisons compterait deux fois la même palette ;
 *   - **un instant se compose et se décompose.** L'API exige un fuseau sur
 *     `fait_le` et sur les dates de litige — décidé le 04/09/2026 après le
 *     défaut où une livraison de 14 h 30 se serait enregistrée à 16 h 30.
 *     L'écran, lui, montre deux champs : un jour et une heure.
 */

import { metresLineaires, mlLisible } from '@stafa/core';

/** Un ordre de transport, tel que l'API le rend. */
export interface OrdreTransport {
  readonly id: string;
  readonly numero: string;
  readonly client_id: string;
  readonly client: string;
  /** L'activité : obligatoire en base, donc jamais nulle. */
  readonly centre_id: string;
  readonly centre: string;
  readonly reference_client: string | null;
  readonly gabarit_code: string | null;
  readonly gabarit: string | null;
  /**
   * LES VÉHICULES ACCEPTÉS — lot 154, 03/11/2026. Codes du référentiel ; le
   * premier est `gabarit_code`. `null` (ou absent, sur une réponse plus
   * ancienne) : seul `gabarit_code` compte.
   */
  readonly gabarits_acceptes?: readonly string[] | null;
  readonly marchandise: string | null;
  /** `numeric` rendu en chaîne : jamais converti en flottant. */
  readonly prix_vente: string | null;
  /**
   * L'INDEXATION FORCÉE — F3, 01/11/2026. `null` : la grille décide ;
   * `false` : retirée à la main ; `true` : demandée à la main.
   */
  readonly indexation?: boolean | null;
  /**
   * LE TOTAL HT AFFICHÉ dans la liste — 02/11/2026 : prix indexé (sinon prix
   * de vente) + prestations annexes, calculé par l'API. Absent de la fiche :
   * `prix_vente` reste le prix convenu.
   */
  readonly total_ht?: string | null;
  readonly distance_km: number | null;
  readonly statut: string;
  readonly note: string | null;
  /** Calculés des étapes par l'API. `null` tant qu'il n'y a pas d'étape. */
  readonly jour_debut: string | null;
  readonly jour_fin: string | null;
  readonly ville_depart: string | null;
  readonly ville_arrivee: string | null;
  /**
   * Le premier CHARGEMENT et la dernière LIVRAISON par rang — à ne pas
   * confondre avec les quatre champs ci-dessus, qui donnent le premier et le
   * dernier arrêt quel que soit leur type. Sur un relais ou une ramasse, ce
   * n'est pas la même étape.
   *
   * `null` quand l'ordre n'a pas d'étape de ce type.
   */
  readonly chargement_jour: string | null;
  readonly chargement_heure: string | null;
  readonly chargement_site: string | null;
  readonly chargement_code_postal: string | null;
  readonly chargement_ville: string | null;
  readonly livraison_jour: string | null;
  readonly livraison_heure: string | null;
  readonly livraison_site: string | null;
  readonly livraison_code_postal: string | null;
  readonly livraison_ville: string | null;
  /**
   * L'AFFRÈTEMENT EN COURS, s'il y en a un. `null` sur un ordre fait en propre.
   *
   * C'est ce qui distingue « Affrété · DUMONT » de « En propre » dans la
   * colonne Traité par — la colonne qui a remplacé le module Affrètement le
   * 05/09/2026.
   *
   * `prix_achat` est ici, `prix_vente` plus haut : la marge se calcule des
   * deux et n'est stockée nulle part.
   */
  /**
   * L'AXE DÉDUIT, au format des zones : « FR45 → ES28 ». Calculé par l'API, qui
   * traduit le pays avec la table `pays` — l'écran ne le recalcule pas.
   *
   * `null` quand le pays manque sur l'étape : on ne devine pas.
   */
  readonly zone_depart: string | null;
  readonly zone_arrivee: string | null;
  readonly affretement_id: string | null;
  readonly affrete_par: string | null;
  readonly prix_achat: string | null;
  readonly etapes: number;
  readonly litiges_ouverts: number;
  /**
   * LA FACTURATION, en deux informations et pas une de plus.
   *
   * Demande de l'utilisateur du 09/09/2026 : « ne donne pas le numéro de
   * facture, seulement la vignette ». Le tableau des ordres ne gagne donc
   * AUCUNE colonne — ces deux champs ne servent qu'à poser deux pastilles.
   *
   * `envoye_facturation_le` est un GESTE : quelqu'un a poussé l'ordre vers la
   * compta. `facture` se DÉDUIT d'une ligne de facture qui porte cet ordre ; il
   * n'est stocké nulle part, et redevient faux si le brouillon est effacé.
   */
  readonly envoye_facturation_le: string | null;
  readonly envoye_facturation_par: string | null;
  readonly facture: boolean;
  readonly cree_le: string;
  readonly modifie_le: string;
}

export type TypeEtape = 'chargement' | 'livraison';

export interface Etape {
  readonly id: string;
  readonly ot_id: string;
  readonly rang: number;
  readonly type: string;
  readonly site_id: string;
  /** L'adresse FIGÉE du jour, recopiée du site à la création. */
  readonly site_nom: string;
  readonly site_adresse: string | null;
  readonly site_code_postal: string | null;
  readonly site_ville: string | null;
  /**
   * Le pays, figé comme les quatre lignes ci-dessus. `null` sur les étapes
   * saisies avant le 06/09/2026 : on ne devine pas un pays, et « 28906 » ne dit
   * pas l'Espagne. C'est ce champ qui permet de déduire l'axe international
   * d'un affrètement.
   */
  readonly site_pays: string | null;
  readonly jour: string;
  readonly heure_debut: string | null;
  readonly heure_fin: string | null;
  readonly quantite: string | null;
  readonly support: string | null;
  readonly poids: number | null;
  /**
   * LE CAMION DE CETTE ÉTAPE — sur l'étape et non sur l'ordre, décidé par
   * l'utilisateur le 04/09/2026 : « le relais doit être possible ». Dans la
   * base v1, 3 missions sur 94 portent deux tracteurs différents.
   */
  readonly vehicule_id: string | null;
  readonly vehicule: string | null;
  /**
   * Le chauffeur et la remorque de CETTE étape, FIGÉS.
   *
   * Recopiés de l'attelage du jour au moment où l'on pose le camion, puis plus
   * jamais relus — décidé par l'utilisateur le 04/09/2026 : « changer un
   * attelage ne doit pas modifier les OT du passé ». Même règle que les quatre
   * lignes d'adresse du site, juste au-dessus.
   */
  readonly chauffeur_id: string | null;
  readonly chauffeur: string | null;
  readonly remorque_id: string | null;
  readonly remorque: string | null;
  readonly fait: boolean;
  /**
   * LES DEUX INSTANTS DU PASSAGE, depuis le 09/09/2026. Ils remplacent
   * `fait_le` : leur différence est le temps réellement passé sur le site, et
   * c'est la donnée qui manquait au coût de revient.
   *
   * `null` tous les deux sur une étape non validée, et `arrive_le` seul peut
   * manquer sur une étape validée avant cette date — on ne l'invente pas.
   */
  readonly arrive_le: string | null;
  readonly reparti_le: string | null;
  /**
   * L'ÉCHANGE DE PALETTES DE CETTE ÉTAPE, s'il y en a un.
   *
   * Rendu par la fiche de l'ordre, mais écrit par le module Emballage
   * (`PUT /emballage/etapes/:id`) : le cadrage §6 veut la saisie sur l'étape,
   * et le calcul du solde dans son module. On le montre ici pour pouvoir le
   * corriger, pas pour le recalculer.
   */
  readonly echange_support: string | null;
  readonly echange_livrees: number | null;
  readonly echange_reprises: number | null;
  readonly echange_bon_id: string | null;
  readonly echange_bon_numero: string | null;
  /**
   * LE LITIGE VIT DANS SA TABLE depuis le 07/09/2026. La fiche montre LE PLUS
   * RÉCENT de l'étape, résolu ou non, sous les mêmes noms de champs qu'avant —
   * seule leur source a changé. `litige_id` est nouveau : on résout CE
   * litige-là, et non « le litige de cette étape », qui n'est plus unique.
   */
  readonly litige_id: string | null;
  readonly motif_litige_id: string | null;
  readonly motif_litige: string | null;
  readonly litige_note: string | null;
  readonly litige_ouvert_le: string | null;
  readonly litige_resolu_le: string | null;
  readonly litige_resolution: string | null;
  /** La référence attendue par le site à cet arrêt — F2, 31/10/2026. */
  readonly reference: string | null;
  readonly note: string | null;
}

/** L'ordre AVEC ses étapes : l'API ne rend jamais l'un sans les autres. */
export interface FicheOrdre {
  readonly ordre_transport: OrdreTransport;
  readonly etapes: readonly Etape[];
  /** Les lignes du Packaging — F4, 01/11/2026. Vide pour un ordre ancien. */
  readonly marchandises?: readonly MarchandiseLue[];
}

/** Un motif de litige, propre à la société. */
export interface MotifLitige {
  readonly id: string;
  readonly nom: string;
  readonly actif: boolean;
  /**
   * Le nombre d'étapes qui le portent, calculé par l'API. Ajouté le 04/09/2026
   * avec l'écran des Paramètres, qui l'affiche et s'en sert pour savoir ce qui
   * peut encore se supprimer. Facultatif : les écrans qui ne l'emploient pas
   * n'ont pas à le connaître.
   */
  readonly emploi?: number;
}

// ---------------------------------------------------------------------------
// Les statuts
// ---------------------------------------------------------------------------

/**
 * Les cinq statuts, dans l'ordre du cycle de vie.
 *
 * La liste est FIGÉE, ici comme dans l'API et comme en base — c'est la seule du
 * dépôt à l'être, et l'exception est expliquée dans
 * `apps/api/src/ordres_transport.ts` : le logiciel raisonne dessus.
 *
 * `classe` nomme la couleur, elle ne la contient pas : les valeurs sont dans la
 * feuille de style, et une copie ici finirait par diverger.
 *
 * Cette liste sert à MONTRER un statut et à FILTRER la liste. Elle ne sert plus
 * à en choisir un depuis le 04/09/2026 : les quatre premiers se déduisent des
 * étapes, et « Annulé » se pose par le bouton de la fiche.
 */
export const STATUTS: readonly {
  readonly cle: string;
  readonly nom: string;
  readonly classe: string;
}[] = [
  { cle: 'attente', nom: 'Attente', classe: 'attente' },
  { cle: 'planifie', nom: 'Planifié', classe: 'planifie' },
  { cle: 'en_cours', nom: 'En cours', classe: 'encours' },
  { cle: 'livre', nom: 'Livré', classe: 'livre' },
  { cle: 'annule', nom: 'Annulé', classe: 'annule' },
];

/**
 * Le statut en français.
 *
 * Rend le code brut si le statut est inconnu, plutôt que rien : le jour où
 * l'API en ajoute un, l'écran l'affiche tel quel au lieu de laisser une case
 * vide dont personne ne comprendrait la cause.
 */
export function statutDit(statut: string): string {
  return STATUTS.find((s) => s.cle === statut)?.nom ?? statut;
}

export function classeStatut(statut: string): string {
  return STATUTS.find((s) => s.cle === statut)?.classe ?? 'attente';
}

// ---------------------------------------------------------------------------
// Les dates, les heures, les instants
// ---------------------------------------------------------------------------

/** « 2026-09-04 » → « 04/09/2026 ». `null` si la date est illisible. */
export function enFrancais(iso: string | null): string | null {
  if (iso === null) return null;
  const morceaux = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (morceaux === null) return null;
  return `${morceaux[3] as string}/${morceaux[2] as string}/${morceaux[1] as string}`;
}

/** « 2026-09-04 » → « 04/09 ». Pour les créneaux, où l'année est comprise. */
export function jourCourt(iso: string | null): string | null {
  if (iso === null) return null;
  const morceaux = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (morceaux === null) return null;
  return `${morceaux[3] as string}/${morceaux[2] as string}`;
}

/**
 * « 08:00:00 » → « 08:00 ».
 *
 * L'API rend toujours les secondes, parce que c'est ce que PostgreSQL écrit et
 * que « 08:00 » et « 08:00:00 » se compareraient autrement. Personne ne veut
 * les lire sur un créneau de quai.
 */
export function heureCourte(heure: string | null): string | null {
  if (heure === null) return null;
  const morceaux = /^(\d{2}):(\d{2})/.exec(heure);
  if (morceaux === null) return null;
  return `${morceaux[1] as string}:${morceaux[2] as string}`;
}

/** « 08:00 → 09:30 », « à partir de 08:00 », « jusqu'à 09:30 », ou `null`. */
export function creneau(etape: {
  readonly heure_debut: string | null;
  readonly heure_fin: string | null;
}): string | null {
  const debut = heureCourte(etape.heure_debut);
  const fin = heureCourte(etape.heure_fin);
  if (debut !== null && fin !== null) return `${debut} → ${fin}`;
  if (debut !== null) return `à partir de ${debut}`;
  if (fin !== null) return `jusqu’à ${fin}`;
  return null;
}

/**
 * Le jour d'aujourd'hui en `AAAA-MM-JJ`, DANS LE FUSEAU DE LA PERSONNE.
 *
 * Pas `toISOString()`, qui rendrait le jour à Greenwich : le 1er janvier à
 * 00 h 30 en France, il donnerait le 31 décembre. Même fonction que dans
 * `affectation.ts` ; elle y est répétée plutôt qu'importée pour ne pas faire
 * dépendre l'écran des ordres de celui des affectations, qui n'ont rien à voir.
 */
export function aujourdhui(maintenant: Date = new Date()): string {
  const mois = String(maintenant.getMonth() + 1).padStart(2, '0');
  const jour = String(maintenant.getDate()).padStart(2, '0');
  return `${maintenant.getFullYear()}-${mois}-${jour}`;
}

/** Le premier et le dernier jour du mois qui contient `jour`. */
export function moisDe(jour: string): { readonly du: string; readonly au: string } {
  const morceaux = /^(\d{4})-(\d{2})-\d{2}$/.exec(jour);
  if (morceaux === null) return { du: jour, au: jour };
  const annee = Number(morceaux[1]);
  const mois = Number(morceaux[2]);
  // Le jour 0 du mois suivant est le dernier du mois courant — y compris en
  // février d'une année bissextile, qu'on n'a donc pas à savoir compter.
  const dernier = new Date(Date.UTC(annee, mois, 0)).getUTCDate();
  const deux = (n: number) => String(n).padStart(2, '0');
  return {
    du: `${morceaux[1] as string}-${morceaux[2] as string}-01`,
    au: `${morceaux[1] as string}-${morceaux[2] as string}-${deux(dernier)}`,
  };
}

/**
 * Compose l'INSTANT que l'API attend, à partir des deux champs de l'écran.
 *
 * L'API exige un fuseau — `2026-09-10T14:30` sans fuseau serait compris à
 * Greenwich, et une livraison faite à 14 h 30 en France s'enregistrerait à
 * 16 h 30. `toISOString()` en pose un, `Z`, et convertit correctement : le
 * constructeur `Date` interprète les composantes dans le fuseau de la machine,
 * qui est celui de la personne devant l'écran.
 *
 * Rend `null` si l'un des deux champs manque : un instant sans heure n'existe
 * pas, et poser midi d'office inventerait une donnée.
 */
export function instantDepuis(jour: string, heure: string): string | null {
  const j = /^(\d{4})-(\d{2})-(\d{2})$/.exec(jour);
  const h = /^(\d{2}):(\d{2})/.exec(heure);
  if (j === null || h === null) return null;

  const annee = Number(j[1]);
  const mois = Number(j[2]);
  const jourDuMois = Number(j[3]);
  const date = new Date(annee, mois - 1, jourDuMois, Number(h[1]), Number(h[2]));
  if (Number.isNaN(date.getTime())) return null;

  // `new Date(2026, 1, 31)` NE SE PLAINT PAS : elle rend le 3 mars. Sans cette
  // comparaison, un 31 février composerait un instant décalé de trois jours,
  // en silence. Trouvé par un test le 04/09/2026 ; c'est la même parade que
  // `dateDuJour` côté API. Le champ `<input type="date">` ne laisse pas taper
  // une telle date, mais la fonction ne doit pas dépendre de qui l'appelle.
  if (
    date.getFullYear() !== annee ||
    date.getMonth() !== mois - 1 ||
    date.getDate() !== jourDuMois
  ) {
    return null;
  }

  return date.toISOString();
}

/** Le jour d'un instant, en `AAAA-MM-JJ`, dans le fuseau de la personne. */
export function jourDe(instant: string | null): string {
  if (instant === null) return '';
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return '';
  return aujourdhui(date);
}

/** L'heure d'un instant, en `HH:MM`, dans le fuseau de la personne. */
export function heureDe(instant: string | null): string {
  if (instant === null) return '';
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return '';
  const deux = (n: number) => String(n).padStart(2, '0');
  return `${deux(date.getHours())}:${deux(date.getMinutes())}`;
}

/** « 03/09/2026 à 08:42 » — un instant lu par un humain. */
export function instantDit(instant: string | null): string | null {
  if (instant === null) return null;
  const jour = enFrancais(jourDe(instant));
  const heure = heureDe(instant);
  if (jour === null || heure === '') return null;
  return `${jour} à ${heure}`;
}

// ---------------------------------------------------------------------------
// Ce qu'on lit d'un ordre
// ---------------------------------------------------------------------------

/**
 * Les dates de l'ordre : la première, et la seconde SEULEMENT si elle diffère.
 *
 * La deuxième ligne du tableau n'apparaît que si le transport passe la nuit —
 * demandé sur la maquette. Répéter la même date deux fois ferait croire à un
 * ordre sur deux jours.
 */
export function datesDites(ordre: {
  readonly jour_debut: string | null;
  readonly jour_fin: string | null;
}): { readonly premiere: string | null; readonly seconde: string | null } {
  const premiere = enFrancais(ordre.jour_debut);
  const fin = enFrancais(ordre.jour_fin);
  return {
    premiere,
    seconde: fin !== null && fin !== premiere ? fin : null,
  };
}

/** « Rouen » et « → Amiens ». `null` de part et d'autre s'il n'y a pas d'étape. */
export function trajetDit(ordre: {
  readonly ville_depart: string | null;
  readonly ville_arrivee: string | null;
}): { readonly depart: string | null; readonly arrivee: string | null } {
  const depart = ordre.ville_depart;
  const arrivee = ordre.ville_arrivee;
  return {
    depart,
    // Un aller-retour sur la même ville n'a pas à s'écrire deux fois.
    arrivee: arrivee !== null && arrivee !== depart ? arrivee : null,
  };
}

/**
 * Un bout de l'ordre, tel que le tableau l'affiche : une date, une heure, un
 * site, une localité.
 *
 * Quatre champs séparés et non une phrase toute faite : la maquette du
 * 05/09/2026 met la date et l'heure dans une colonne, le site et la localité
 * dans la suivante, l'heure et la localité en second rang gris. Les recoller
 * ici obligerait l'écran à les redécouper.
 */
export interface BoutDOrdre {
  readonly jour: string | null;
  readonly heure: string | null;
  readonly site: string | null;
  /** « 45770 Saran ». `null` si ni code postal ni ville ne sont renseignés. */
  readonly localite: string | null;
}

function bout(
  jour: string | null,
  heure: string | null,
  site: string | null,
  codePostal: string | null,
  ville: string | null,
): BoutDOrdre {
  const localite = [codePostal, ville]
    .filter((m): m is string => m !== null && m.trim() !== '')
    .join(' ');

  return {
    jour: enFrancais(jour),
    heure: heureCourte(heure),
    site: site !== null && site.trim() !== '' ? site : null,
    localite: localite !== '' ? localite : null,
  };
}

/**
 * Le premier chargement de l'ordre — colonnes « Chargement » et « Adresse de
 * chargement » du tableau.
 *
 * Tout à `null` sur un ordre qui n'a pas encore d'étape de chargement. Le
 * tableau affiche alors le tiret d'absence, il ne cache pas la ligne.
 */
export function chargementDit(ordre: {
  readonly chargement_jour: string | null;
  readonly chargement_heure: string | null;
  readonly chargement_site: string | null;
  readonly chargement_code_postal: string | null;
  readonly chargement_ville: string | null;
}): BoutDOrdre {
  return bout(
    ordre.chargement_jour,
    ordre.chargement_heure,
    ordre.chargement_site,
    ordre.chargement_code_postal,
    ordre.chargement_ville,
  );
}

/** La dernière livraison de l'ordre. Même règle que `chargementDit`. */
export function livraisonDit(ordre: {
  readonly livraison_jour: string | null;
  readonly livraison_heure: string | null;
  readonly livraison_site: string | null;
  readonly livraison_code_postal: string | null;
  readonly livraison_ville: string | null;
}): BoutDOrdre {
  return bout(
    ordre.livraison_jour,
    ordre.livraison_heure,
    ordre.livraison_site,
    ordre.livraison_code_postal,
    ordre.livraison_ville,
  );
}

/** Les quatre lignes d'adresse d'une étape, celles qui sont renseignées. */
export function adresseDite(etape: Etape): readonly string[] {
  const codePostalEtVille = [etape.site_code_postal, etape.site_ville]
    .filter((m): m is string => m !== null && m.trim() !== '')
    .join(' ');

  return [etape.site_nom, etape.site_adresse ?? '', codePostalEtVille].filter(
    (ligne) => ligne.trim() !== '',
  );
}

/**
 * LE LIEN D'ITINÉRAIRE GPS d'une étape — lot 133, maquette téléphone du
 * 01/11/2026.
 *
 * Le lien « universel » de Google Maps (`/maps/dir/?api=1`) : sur un
 * téléphone il ouvre l'application de navigation, sur un ordinateur la carte
 * dans le navigateur. Il ne porte QUE l'adresse — rue, code postal, ville —
 * et non le nom du site, que la recherche de carte confond volontiers avec
 * un commerce du même nom.
 *
 * `null` quand l'étape n'a ni rue, ni code postal, ni ville : un lien vers
 * une destination vide ouvrirait une carte sans rien dessus.
 */
export function lienItineraire(etape: {
  readonly site_adresse: string | null;
  readonly site_code_postal: string | null;
  readonly site_ville: string | null;
}): string | null {
  const morceaux = [etape.site_adresse, etape.site_code_postal, etape.site_ville]
    .filter((m): m is string => m !== null && m.trim() !== '')
    .map((m) => m.trim());
  if (morceaux.length === 0) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(morceaux.join(' '))}`;
}

/**
 * L'heure de MAINTENANT, en `HH:MM`, dans le fuseau de la personne.
 *
 * Sert à pré-remplir la boîte de validation : on valide presque toujours une
 * étape au moment où elle vient d'être faite, et retaper l'heure serait un
 * geste de plus à chaque étape.
 */
export function maintenant(horloge: Date = new Date()): string {
  const deux = (n: number) => String(n).padStart(2, '0');
  return `${deux(horloge.getHours())}:${deux(horloge.getMinutes())}`;
}

/**
 * Ce qui roule sur cette étape, en une phrase : « GZ-083-NF · Patrick Perrin ».
 *
 * `null` tant qu'aucun camion n'est posé — l'étape n'est pas encore planifiée,
 * et c'est le cas ordinaire à la saisie. Le chauffeur et la remorque ne sont
 * ajoutés que s'ils existent : un camion sans attelage ce jour-là se dit
 * quand même, il ne s'invente pas de conducteur.
 */
export function ressourceDite(etape: {
  readonly vehicule: string | null;
  readonly chauffeur: string | null;
  readonly remorque: string | null;
}): string | null {
  if (etape.vehicule === null) return null;
  const suite = [etape.remorque, etape.chauffeur].filter(
    (m): m is string => m !== null && m.trim() !== '',
  );
  return suite.length === 0
    ? etape.vehicule
    : `${etape.vehicule} · ${suite.join(' · ')}`;
}

/** « Ouvert le 03/09/2026 à 08:42 · non résolu », ou « … · résolu le … ». */
export function litigeDit(etape: Etape): string | null {
  if (etape.motif_litige_id === null) return null;
  const ouvert = instantDit(etape.litige_ouvert_le);
  const resolu = instantDit(etape.litige_resolu_le);
  const debut = ouvert === null ? 'Ouvert' : `Ouvert le ${ouvert}`;
  return resolu === null ? `${debut} · non résolu` : `${debut} · résolu le ${resolu}`;
}

/**
 * Les totaux de marchandise, SUR LES CHARGEMENTS SEULS.
 *
 * Sommer aussi les livraisons compterait deux fois la même palette : elle est
 * chargée puis livrée. Le support n'est rendu que si tous les chargements
 * portent le même — sinon « 24 Palette 80×120 » mentirait sur la moitié du
 * chargement.
 */
export function totaux(etapes: readonly Etape[]): {
  readonly quantite: number | null;
  readonly support: string | null;
  readonly poids: number | null;
} {
  const chargements = etapes.filter((e) => e.type === 'chargement');

  const quantites = chargements
    .map((e) => (e.quantite === null ? null : Number(e.quantite)))
    .filter((n): n is number => n !== null && Number.isFinite(n));
  const poids = chargements
    .map((e) => e.poids)
    .filter((n): n is number => n !== null && Number.isFinite(n));

  const supports = new Set(
    chargements
      .map((e) => e.support)
      .filter((s): s is string => s !== null && s.trim() !== ''),
  );

  return {
    quantite: quantites.length === 0 ? null : quantites.reduce((a, b) => a + b, 0),
    support: supports.size === 1 ? ([...supports][0] as string) : null,
    poids: poids.length === 0 ? null : poids.reduce((a, b) => a + b, 0),
  };
}

/**
 * Un rapport du prix à une quantité — à la palette, au kilomètre.
 *
 * Rend une CHAÎNE à deux décimales, jamais un nombre : elle est destinée à être
 * affichée, et l'appelant n'a pas à choisir un arrondi. `null` dès que le
 * calcul n'a pas de sens — pas de prix, pas de quantité, division par zéro.
 */
export function rapport(prix: string | null, quantite: number | null): string | null {
  if (prix === null || quantite === null || quantite === 0) return null;
  const montant = Number(prix);
  if (!Number.isFinite(montant)) return null;
  return (montant / quantite).toFixed(2).replace('.', ',');
}

/** « 12 400 » — un nombre lu par un humain, espaces de milliers compris. */
export function lisible(nombre: number | null): string | null {
  if (nombre === null || !Number.isFinite(nombre)) return null;
  return new Intl.NumberFormat('fr-FR').format(nombre);
}

/** « 610,00 € ». L'API rend un `numeric` en chaîne — jamais un flottant. */
export function enEuros(montant: string | null): string | null {
  if (montant === null || montant.trim() === '') return null;
  const nombre = Number(montant);
  if (!Number.isFinite(nombre)) return montant;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(nombre);
}

// ---------------------------------------------------------------------------
// La liste
// ---------------------------------------------------------------------------

/** Sans accent ni casse : « Alizay » se trouve en tapant « alizay ». */
function aplatir(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * Filtre la liste DÉJÀ CHARGÉE, sur tout ce qui se voit à l'écran.
 *
 * Le filtrage est local, comme dans les annuaires : la fenêtre de dates borne
 * déjà ce que l'API rend, et rappeler l'API à chaque frappe ferait clignoter la
 * liste pour un calcul que le navigateur fait en une milliseconde.
 */
export function filtrer(
  ordres: readonly OrdreTransport[],
  recherche: string,
): readonly OrdreTransport[] {
  const terme = aplatir(recherche.trim());
  if (terme === '') return ordres;

  return ordres.filter((o) =>
    [
      o.numero,
      o.client,
      o.reference_client ?? '',
      o.ville_depart ?? '',
      o.ville_arrivee ?? '',
      // Les lieux du tableau : chercher « Lesquin » ne trouverait rien alors
      // que le mot est à l'écran. Les villes de départ et d'arrivée ne les
      // couvrent pas — ce ne sont pas les mêmes étapes sur un relais.
      o.chargement_site ?? '',
      o.chargement_code_postal ?? '',
      o.chargement_ville ?? '',
      o.livraison_site ?? '',
      o.livraison_code_postal ?? '',
      o.livraison_ville ?? '',
      o.gabarit ?? '',
      o.marchandise ?? '',
    ].some((champ) => aplatir(champ).includes(terme)),
  );
}

/** Du plus récent au plus ancien : le numéro croît avec le temps. */
export function ranger(ordres: readonly OrdreTransport[]): readonly OrdreTransport[] {
  return [...ordres].sort((a, b) => b.numero.localeCompare(a.numero, 'fr'));
}

/** « 94 ordres de transport — dont 4 en cours, 12 planifiés ». */
export function decompte(
  ordres: readonly OrdreTransport[],
  surCombien?: number,
): string {
  const total = ordres.length;
  let tete = total > 1 ? `${total} ordres de transport` : `${total} ordre de transport`;

  /**
   * LE « SUR N » SE COLLE AU TOTAL, ET NULLE PART AILLEURS.
   *
   * Constaté À LA CAPTURE le 10/09/2026 : posé à la fin de la phrase, il
   * donnait « 1 ordre de transport — dont 1 planifié SUR 6 », où il avait
   * l'air de compter les planifiés. Le chiffre était juste et la phrase
   * fausse.
   *
   * Il ne s'écrit que s'il apprend quelque chose : quand rien n'est écarté,
   * « 6 ordres sur 6 » n'est que du bruit.
   */
  if (surCombien !== undefined && surCombien !== total) {
    tete += ` sur ${surCombien}`;
  }

  const enCours = ordres.filter((o) => o.statut === 'en_cours').length;
  const planifies = ordres.filter((o) => o.statut === 'planifie').length;
  const litiges = ordres.filter((o) => o.litiges_ouverts > 0).length;

  const details: string[] = [];
  if (enCours > 0) details.push(`${enCours} en cours`);
  if (planifies > 0)
    details.push(planifies > 1 ? `${planifies} planifiés` : '1 planifié');
  if (litiges > 0) {
    details.push(litiges > 1 ? `${litiges} avec litige` : '1 avec litige');
  }

  return details.length === 0 ? tete : `${tete} — dont ${details.join(', ')}`;
}

// ---------------------------------------------------------------------------
// Les supports
// ---------------------------------------------------------------------------

/**
 * Les supports de charge, LISTE FERMÉE À L'ÉCRAN — la base, elle, reste
 * ouverte.
 *
 * Demandé par l'utilisateur le 04/09/2026 : « les support via liste
 * déroulable ». **Cela contredit un commentaire de la migration
 * `20260904090000`**, qui écrivait : « `support` est un texte : la liste fermée
 * viendra avec le module Paramètres, et la figer ici imposerait les emballages
 * de TNS à tous les clients de Stafa ». La contradiction a été signalée avant
 * d'écrire, et la voie retenue est celle DÉJÀ EN USAGE dans ce dépôt pour les
 * pays et les conditions de paiement : la règle n'existe que dans l'interface,
 * la colonne reste un `text` sans contrainte. Le jour où le module Paramètres
 * existera, cette constante deviendra une table et rien d'autre ne bougera.
 *
 * POURQUOI FERMER : dans la base v1, le support d'une étape s'écrit
 * `Palette 80×120` 150 fois, `Palette(s)` 14 fois, `MPL` 14 fois et `Mlp`
 * 2 fois — **quatre orthographes pour deux choses**. Rien ne se compte sur une
 * colonne écrite ainsi.
 *
 * CE QUE PORTE CETTE LISTE, et d'où ça vient — l'utilisateur a répondu le
 * 04/09/2026 : « les formats de palettes les plus utilisés et le ML » :
 *
 *   - `Palette 80×120` : CONSTATÉE, 150 fois dans la base v1 ;
 *   - `Palette 100×120` et `Demi-palette 60×80` : les deux autres formats
 *     courants du transport routier français. **Non constatés dans la base
 *     v1** — proposés, et à corriger si l'un ne sert pas ;
 *   - `ML`, le mètre linéaire. Écrit `MPL` 14 fois et `Mlp` 2 fois dans la
 *     base v1 ; l'utilisateur dit `ML`, c'est donc `ML`. Les étapes anciennes
 *     qui portent l'ancienne orthographe la gardent : `supportsAvec` la remet
 *     en tête de liste plutôt que de la faire disparaître.
 *
 * **CE QUI N'EST PAS FAIT** : l'utilisateur précise qu'un ML « ne peut jamais
 * dépasser la limite du camion ». Aucun contrôle ne le vérifie, et c'est
 * délibéré — la longueur de plancher d'un gabarit n'est nulle part en base, et
 * poser 13,6 m en dur imposerait la semi de TNS à tous les clients de Stafa.
 * Le contrôle viendra quand le référentiel des gabarits portera une longueur.
 */
/**
 * La liste à montrer, la valeur enregistrée COMPRISE même si elle n'y est plus.
 *
 * Les supports VIENNENT DE LA SOCIÉTÉ depuis le 04/09/2026 (migration
 * 20260904170000) : la liste de quatre valeurs qui vivait ici était celle de
 * TNS, servie à tous les clients de Stafa. Elle a été reprise en base, où
 * chacun la modifie.
 *
 * Ce qui reste ici est la seule règle d'écran : garder la valeur enregistrée
 * en tête de liste quand elle n'y figure plus. Sans cela, ouvrir une étape
 * ancienne afficherait autre chose que ce qui est enregistré, et un simple
 * aller-retour changerait le support sans que personne l'ait demandé.
 *
 * **CE QUI N'EST TOUJOURS PAS FAIT** : l'utilisateur précise qu'un ML « ne peut
 * jamais dépasser la limite du camion ». Aucun contrôle ne le vérifie — la
 * longueur de plancher d'un gabarit n'est nulle part en base, et poser 13,6 m
 * en dur imposerait la semi de TNS à tous les clients de Stafa.
 */
export function supportsAvec(
  valeurActuelle: string,
  supports: readonly string[],
): readonly string[] {
  const valeur = valeurActuelle.trim();
  if (valeur === '' || supports.includes(valeur)) return supports;
  return [valeur, ...supports];
}

/**
 * La marchandise d'un chargement se REPORTE sur les livraisons qui n'ont rien.
 *
 * Demandé par l'utilisateur le 04/09/2026 : « en tapant sur la case chargement
 * les quantité et support se transposent à la livraison pour faciliter la
 * saisie ». Dans la très grande majorité des transports, ce qui est chargé est
 * ce qui est livré — 87 des 94 missions de la base v1 n'ont qu'un chargement et
 * une livraison.
 *
 * **CE QUI EST DÉJÀ SAISI N'EST JAMAIS ÉCRASÉ.** Une livraison partielle, où
 * l'on dépose 12 des 24 palettes, garde ses 12 : le report est une aide à la
 * saisie, pas une règle de gestion.
 *
 * LE POIDS EST REPORTÉ LUI AUSSI, décidé par l'utilisateur le 04/09/2026 après
 * la question — il n'avait d'abord nommé que la quantité et le support.
 *
 * Rend la liste ENTIÈRE, chargements compris, pour que l'appelant la pose telle
 * quelle.
 */
export function reporterSurLesLivraisons(
  etapes: readonly SaisieEtape[],
  rangModifie: number,
  avant: SaisieEtape,
): readonly SaisieEtape[] {
  const source = etapes[rangModifie];
  if (source === undefined || source.type !== 'chargement') return etapes;
  if (
    source.quantite.trim() === '' &&
    source.support.trim() === '' &&
    source.poids.trim() === ''
  ) {
    return etapes;
  }

  return etapes.map((etape, rang) => {
    if (rang === rangModifie || etape.type !== 'livraison') return etape;
    return {
      ...etape,
      quantite: suitLeChargement(etape.quantite, source.quantite, avant.quantite),
      support: suitLeChargement(etape.support, source.support, avant.support),
      poids: suitLeChargement(etape.poids, source.poids, avant.poids),
    };
  });
}

/**
 * Un champ de la livraison suit-il celui du chargement ?
 *
 * LE DÉFAUT QUE CETTE FONCTION CORRIGE, signalé le 07/09/2026 : « lorsque le
 * logiciel pré-remplit les quantités et poids de la marchandise il ne garde
 * que le premier chiffre ». La saisie se fait FRAPPE PAR FRAPPE, et le report
 * tournait à chacune :
 *
 *   « 3 »  — la livraison est vide, elle reçoit « 3 » ;
 *   « 33 » — la livraison vaut « 3 », donc « déjà saisie », donc protégée.
 *
 * La règle « n'écrase jamais ce qui est saisi » se retournait contre
 * elle-même : ce qu'elle protégeait, c'était sa propre valeur d'une frappe
 * plus tôt. L'utilisateur voyait 33 sur son chargement et 3 sur sa livraison.
 *
 * LA DISTINCTION QUI MANQUAIT : « la livraison porte une valeur » ne dit pas
 * « quelqu'un l'a saisie ». `avant` est la valeur qu'avait le chargement juste
 * avant cette frappe ; si la livraison porte exactement celle-là, c'est le
 * report d'avant qui l'y a mise, et il peut donc la remettre à jour.
 *
 * Ce que cela ne casse pas : une livraison partielle — 12 des 24 palettes —
 * ne vaut ni le vide ni l'ancienne valeur du chargement. Elle reste intacte,
 * et c'était bien la règle voulue le 04/09.
 *
 * `avant` EST OBLIGATOIRE, ET C'EST VOULU. Le rendre facultatif aurait laissé
 * le défaut revenir en silence : un appelant qui l'oublie retrouve exactement
 * le comportement fautif, et aucun test de fonction pure ne peut voir cet
 * oubli — il n'y a pas de test d'écran dans ce dépôt. Obligatoire, c'est le
 * compilateur qui refuse, et il ne se trompe jamais.
 */
function suitLeChargement(
  surLaLivraison: string,
  surLeChargement: string,
  avantLaFrappe: string,
): string {
  if (surLaLivraison.trim() === '') return surLeChargement;
  if (surLaLivraison === avantLaFrappe) return surLeChargement;
  return surLaLivraison;
}

// ---------------------------------------------------------------------------
// La saisie d'un ordre
// ---------------------------------------------------------------------------

/**
 * PAS DE `statut` ICI, ET C'EST LE POINT. Il se déduit des étapes ; le poser
 * dans ce formulaire donnerait une valeur que la première étape validée
 * réécrirait sans prévenir. Annuler et rouvrir sont les deux seules décisions
 * qui restent, et elles se prennent par les boutons de la fiche.
 */
export interface SaisieOrdre {
  readonly client_id: string;
  /**
   * L'ACTIVITÉ, obligatoire depuis le 07/09/2026. Rien ne la pré-remplit —
   * l'utilisateur a choisi de la désigner à chaque ordre : un ordre se crée
   * avant d'être affecté à un camion, donc au moment où le centre est exigé
   * aucun véhicule n'est encore connu, et un pré-remplissage par le véhicule
   * n'aurait rien à lire.
   */
  readonly centre_id: string;
  /**
   * LE NUMÉRO, saisissable À LA CRÉATION SEULEMENT depuis le 07/09/2026.
   * Demande de l'utilisateur : « le n° OT doit pouvoir être éditable pour le
   * moment ». Vide, le compteur attribue — c'est le cas courant.
   */
  readonly numero: string;
  readonly reference_client: string;
  readonly gabarit_code: string;
  /**
   * LES VÉHICULES ACCEPTÉS, codes séparés par des virgules (« T6,TD6 ») —
   * lot 154. Une chaîne et non une liste : toute la saisie est faite de
   * chaînes, que `differenceOrdre` compare après `trim()`. Vide : seul
   * `gabarit_code` compte. Les icônes de la fiche neuve tiennent les deux
   * champs ensemble (`basculerCarrosserie`).
   */
  readonly gabarits_acceptes: string;
  readonly marchandise: string;
  readonly prix_vente: string;
  /**
   * SANS / AVEC, FORCÉ OU NON — F3, 01/11/2026. `''` : la grille décide
   * (rien n'est forcé) ; `'avec'` ou `'sans'` : choisi à la main.
   */
  readonly indexation: '' | 'avec' | 'sans';
  readonly distance_km: string;
  readonly note: string;
}

/**
 * LES TROIS NATURES DE MARCHANDISE — décision du 31/10/2026.
 *
 * Elles s'écrivent dans `ordres_transport.marchandise`, qui existe et reste du
 * texte libre en base : les ordres déjà enregistrés portent d'autres valeurs
 * (« Produits secs »…) et ne sont pas touchés. L'écran les montre telles
 * quelles, en plus des trois choix, plutôt que de les faire disparaître.
 */
export const NATURES_MARCHANDISE = ['Général', 'Frigorifique', 'Dangereux'] as const;

export const ORDRE_VIDE: SaisieOrdre = {
  client_id: '',
  centre_id: '',
  numero: '',
  reference_client: '',
  gabarit_code: '',
  gabarits_acceptes: '',
  // GÉNÉRAL PAR DÉFAUT — décision du 31/10/2026 : la nature de la marchandise
  // se choisit entre Général, Frigorifique et Dangereux, Général en premier.
  marchandise: 'Général',
  prix_vente: '',
  indexation: '',
  distance_km: '',
  note: '',
};

export function ordreDepuis(ordre: OrdreTransport): SaisieOrdre {
  return {
    client_id: ordre.client_id,
    centre_id: ordre.centre_id,
    // Le numéro d'un ordre existant se REGARDE, il ne se remodifie pas : la
    // modification ne l'accepte pas côté API. On le recopie quand même, pour
    // que `differenceOrdre` ne le voie jamais changer.
    numero: ordre.numero,
    reference_client: ordre.reference_client ?? '',
    gabarit_code: ordre.gabarit_code ?? '',
    gabarits_acceptes: (ordre.gabarits_acceptes ?? []).join(','),
    marchandise: ordre.marchandise ?? '',
    prix_vente: ordre.prix_vente ?? '',
    indexation:
      ordre.indexation === true ? 'avec' : ordre.indexation === false ? 'sans' : '',
    distance_km: ordre.distance_km === null ? '' : String(ordre.distance_km),
    note: ordre.note ?? '',
  };
}

/**
 * Contrôle la saisie d'un ordre AVANT de l'envoyer.
 *
 * Il ANTICIPE le contrôle de l'API, il ne le remplace pas : la faute s'affiche
 * sous le champ concerné plutôt que dans un bandeau. Si les deux divergent,
 * c'est l'API qui a raison.
 */
export function validerOrdre(
  saisie: SaisieOrdre,
  initiale?: SaisieOrdre,
): Readonly<Record<string, string>> {
  const fautes: Record<string, string> = {};

  if (saisie.client_id.trim() === '') {
    fautes['client_id'] = 'Choisissez un client.';
  }

  if (saisie.centre_id.trim() === '') {
    fautes['centre_id'] = 'Choisissez une activité.';
  }

  // LE NUMÉRO EST FACULTATIF À LA CRÉATION : vide, le compteur attribue.
  // L'unicité, elle, ne se vérifie qu'en base — c'est l'API qui la dira.
  if (saisie.numero.trim().length > 20) {
    fautes['numero'] = '20 caractères au plus.';
  }

  // MAIS ON NE VIDE PAS UN NUMÉRO DÉJÀ POSÉ. `initiale` n'est fourni qu'à la
  // modification ; s'il portait un numéro et que le champ est vide, l'API ne
  // ferait RIEN — elle traite le vide comme « n'y touche pas ». L'utilisateur
  // croirait avoir effacé. On le lui dit ici plutôt que de le laisser dans
  // cette illusion.
  if (
    initiale !== undefined &&
    initiale.numero.trim() !== '' &&
    saisie.numero.trim() === ''
  ) {
    fautes['numero'] = 'Le numéro ne peut pas être vidé.';
  }

  // LE PRIX EST OBLIGATOIRE depuis le 31/10/2026, comme côté API. Dit ici
  // pour que la faute s'affiche sous le champ, avant l'envoi.
  const prix = saisie.prix_vente.trim().replace(',', '.');
  if (prix === '') {
    fautes['prix_vente'] = 'Le prix de vente est obligatoire.';
  } else if (!/^[0-9]{1,10}(\.[0-9]{1,2})?$/.test(prix)) {
    fautes['prix_vente'] = 'Un montant positif, deux décimales au plus.';
  }

  const km = saisie.distance_km.trim();
  if (km !== '' && !/^[0-9]{1,6}$/.test(km)) {
    fautes['distance_km'] = 'Un nombre entier de kilomètres.';
  }
  if (km !== '' && Number(km) > 100000) {
    fautes['distance_km'] = '100 000 km au plus.';
  }

  if (saisie.marchandise.trim().length > 500) {
    fautes['marchandise'] = '500 caractères au plus.';
  }
  if (saisie.reference_client.trim().length > 120) {
    fautes['reference_client'] = '120 caractères au plus.';
  }
  if (saisie.note.trim().length > 2000) {
    fautes['note'] = '2 000 caractères au plus.';
  }

  return fautes;
}

/** Ce qu'on envoie à la création : les champs vides ne partent pas. */
export function pourCreationOrdre(
  saisie: SaisieOrdre,
): Readonly<Record<string, string | boolean | readonly string[]>> {
  const corps: Record<string, string | boolean | readonly string[]> = {
    client_id: saisie.client_id.trim(),
    centre_id: saisie.centre_id.trim(),
  };
  // Vide, la clé ne part PAS : l'API distingue « absent » — attribue-moi un
  // numéro — de « présent », qu'elle prendrait au mot.
  if (saisie.numero.trim() !== '') {
    corps['numero'] = saisie.numero.trim();
  }
  for (const cle of [
    'reference_client',
    'gabarit_code',
    'marchandise',
    'prix_vente',
    'distance_km',
    'note',
  ] as const) {
    const valeur = saisie[cle].trim();
    if (valeur !== '') {
      corps[cle] = cle === 'prix_vente' ? valeur.replace(',', '.') : valeur;
    }
  }
  // RIEN DE FORCÉ, RIEN N'EST ENVOYÉ : la colonne reste vide et la grille
  // décide.
  if (saisie.indexation !== '') corps['indexation'] = saisie.indexation === 'avec';
  // LA LISTE DES VÉHICULES ACCEPTÉS part en liste (lot 154) : l'API en fait
  // aussi le `gabarit_code`, qui part quand même pour les réponses anciennes.
  const acceptes = codesAcceptes(saisie);
  if (acceptes.length > 0) corps['gabarits_acceptes'] = acceptes;
  return corps;
}

/**
 * LES CODES ACCEPTÉS D'UNE SAISIE — lot 154. La liste si elle existe, sinon le
 * seul `gabarit_code` (une confirmation lue par l'IA n'en remplit qu'un).
 */
export function codesAcceptes(saisie: Pick<SaisieOrdre, 'gabarit_code' | 'gabarits_acceptes'>): readonly string[] {
  // `?? ''` : une saisie construite avant le lot 154 (l'aperçu, un état gardé)
  // n'a pas ce champ ; elle ne doit pas faire tomber l'écran.
  const liste = (saisie.gabarits_acceptes ?? '')
    .split(',')
    .map((c) => c.trim())
    .filter((c) => c !== '');
  if (liste.length > 0) return liste;
  const seul = (saisie.gabarit_code ?? '').trim();
  return seul === '' ? [] : [seul];
}

/**
 * LES QUATRE CARROSSERIES DE LA FICHE NEUVE — lot 154. Ce sont celles du
 * référentiel `vehicules` (migration `20260903160000`) ; le code retenu au
 * clic est celui du SEMI (réponse de Mohamed : « semi par défaut »).
 */
export const CARROSSERIES = [
  { lettres: 'T', nom: 'Tautliner', semi: 'T6' },
  { lettres: 'FRG', nom: 'Fourgon', semi: 'FRG6' },
  { lettres: 'TD', nom: 'Frigorifique', semi: 'TD6' },
  { lettres: 'P', nom: 'Plateau', semi: 'P6' },
] as const;

/** La carrosserie d'un code : « TD6 » → « TD ». */
export function carrosserieDe(code: string): string {
  return code.replace(/[0-9]+$/, '');
}

/** Cette carrosserie est-elle cochée ? Un code d'une autre taille (« T5 ») compte. */
export function carrosserieChoisie(codes: readonly string[], lettres: string): boolean {
  return codes.some((c) => carrosserieDe(c) === lettres);
}

/**
 * UN CLIC SUR UNE ICÔNE — lot 154. Cochée : tous les codes de cette
 * carrosserie partent. Décochée : son semi s'ajoute en fin de liste. Rend les
 * DEUX champs, qui restent ainsi d'accord : `gabarit_code` est le premier.
 */
export function basculerCarrosserie(
  saisie: Pick<SaisieOrdre, 'gabarit_code' | 'gabarits_acceptes'>,
  lettres: string,
): { readonly gabarit_code: string; readonly gabarits_acceptes: string } {
  const avant = codesAcceptes(saisie);
  const carrosserie = CARROSSERIES.find((c) => c.lettres === lettres);
  const apres = carrosserieChoisie(avant, lettres)
    ? avant.filter((c) => carrosserieDe(c) !== lettres)
    : carrosserie === undefined
      ? avant
      : [...avant, carrosserie.semi];
  return { gabarit_code: apres[0] ?? '', gabarits_acceptes: apres.join(',') };
}

/**
 * Ce qui a changé, et rien d'autre.
 *
 * Un champ vidé part à `null` — c'est le contrat de l'API : absent = on n'y
 * touche pas, `null` = on efface. C'est aussi ce qui garde le journal des
 * traces lisible.
 */
export function differenceOrdre(
  initiale: SaisieOrdre,
  courante: SaisieOrdre,
): Readonly<Record<string, string | boolean | null | readonly string[]>> {
  const change: Record<string, string | boolean | null | readonly string[]> = {};
  for (const cle of Object.keys(ORDRE_VIDE) as (keyof SaisieOrdre)[]) {
    const avant = (initiale[cle] ?? '').trim();
    const apres = (courante[cle] ?? '').trim();
    if (avant === apres) continue;
    if (cle === 'indexation') {
      // VIDE REND LA DÉCISION À LA GRILLE (`null`), pas une chaîne vide.
      change[cle] = apres === '' ? null : apres === 'avec';
      continue;
    }
    if (cle === 'gabarits_acceptes') {
      // LA LISTE PART EN LISTE (lot 154), et vide l'efface.
      change[cle] = apres === '' ? null : apres.split(',').map((c) => c.trim()).filter((c) => c !== '');
      continue;
    }
    change[cle] =
      apres === '' ? null : cle === 'prix_vente' ? apres.replace(',', '.') : apres;
  }
  return change;
}

// ---------------------------------------------------------------------------
// La saisie d'une étape
// ---------------------------------------------------------------------------

export interface SaisieEtape {
  readonly type: string;
  readonly vehicule_id: string;
  readonly chauffeur_id: string;
  readonly remorque_id: string;
  readonly site_id: string;
  readonly site_nom: string;
  readonly site_adresse: string;
  readonly site_code_postal: string;
  readonly site_ville: string;
  readonly jour: string;
  readonly heure_debut: string;
  readonly heure_fin: string;
  readonly quantite: string;
  readonly support: string;
  readonly poids: string;
  /**
   * LA RÉFÉRENCE DE L'ÉTAPE — F2, 31/10/2026. Le numéro que le site attend
   * (rendez-vous, commande, booking). Les « instructions » de la maquette sont
   * `note`, juste en dessous : même notion, décidé avec Mohamed le 31/10.
   */
  readonly reference: string;
  readonly note: string;
  /**
   * Coché ou non, et LES DEUX HEURES du passage — arrivée et départ.
   *
   * `reparti_jour` peut rester vide : il vaut alors le jour d'arrivée. C'est
   * le cas de presque toutes les étapes, et le laisser à saisir ferait taper
   * deux fois la même date. On ne le remplit que pour une étape à cheval sur
   * minuit.
   */
  readonly fait: boolean;
  readonly arrive_jour: string;
  readonly arrive_heure: string;
  readonly reparti_jour: string;
  readonly reparti_heure: string;
  // AUCUN CHAMP DE LITIGE ICI depuis le 07/09/2026 : il a sa table et ses
  // routes, et le formulaire d'étape ne l'écrit plus.
}

export function etapeVide(type: TypeEtape, jour: string): SaisieEtape {
  return {
    type,
    vehicule_id: '',
    chauffeur_id: '',
    remorque_id: '',
    site_id: '',
    site_nom: '',
    site_adresse: '',
    site_code_postal: '',
    site_ville: '',
    jour,
    heure_debut: '',
    heure_fin: '',
    quantite: '',
    support: '',
    poids: '',
    reference: '',
    note: '',
    fait: false,
    arrive_jour: '',
    arrive_heure: '',
    reparti_jour: '',
    reparti_heure: '',
  };
}

export function etapeDepuis(etape: Etape): SaisieEtape {
  return {
    type: etape.type,
    vehicule_id: etape.vehicule_id ?? '',
    chauffeur_id: etape.chauffeur_id ?? '',
    remorque_id: etape.remorque_id ?? '',
    site_id: etape.site_id,
    site_nom: etape.site_nom,
    site_adresse: etape.site_adresse ?? '',
    site_code_postal: etape.site_code_postal ?? '',
    site_ville: etape.site_ville ?? '',
    jour: etape.jour,
    heure_debut: heureCourte(etape.heure_debut) ?? '',
    heure_fin: heureCourte(etape.heure_fin) ?? '',
    quantite: etape.quantite ?? '',
    support: etape.support ?? '',
    poids: etape.poids === null ? '' : String(etape.poids),
    reference: etape.reference ?? '',
    note: etape.note ?? '',
    fait: etape.fait,
    arrive_jour: jourDe(etape.arrive_le),
    arrive_heure: heureDe(etape.arrive_le),
    reparti_jour: jourDe(etape.reparti_le),
    reparti_heure: heureDe(etape.reparti_le),
  };
}

// ---------------------------------------------------------------------------
// DUPLIQUER UN ORDRE
// ---------------------------------------------------------------------------

/**
 * RECOPIER UN ORDRE POUR NE PAS LE RESAISIR.
 *
 * Demandé par l'utilisateur le 08/09/2026 : « il faut mettre la duplication
 * des OT pour éviter de réécrire 50 fois la même chose ». Le même client, le
 * même site, la même marchandise, le même prix reviennent semaine après
 * semaine ; seule la date change.
 *
 *
 * CE N'EST PAS UNE CRÉATION, C'EST UN PRÉ-REMPLISSAGE
 * ---------------------------------------------------------------------------
 * Rien ne part en base ici. Ces fonctions rendent la SAISIE du formulaire de
 * création, que l'utilisateur relit et corrige avant d'enregistrer. Créer
 * directement en base aurait été plus court d'un clic et beaucoup plus
 * dangereux : un ordre nait alors sans que personne n'ait regardé ce qu'il
 * contient, et il apparaît aussitôt sur le planning.
 *
 * Conséquence : **aucune route d'API, aucune migration.** Le formulaire de
 * création existant fait le reste, avec ses contrôles.
 *
 *
 * DEUX CHOSES NE SE RECOPIENT JAMAIS, ET C'EST VOULU
 * ---------------------------------------------------------------------------
 * **Le numéro.** Vide, l'API en attribue un neuf depuis `compteurs_ot` — c'est
 * le contrat de `pourCreationOrdre`, qui n'envoie pas la clé quand le champ est
 * vide. Recopier le numéro ferait refuser la création sur l'unicité, avec un
 * message que personne ne rattacherait à la duplication.
 *
 * **Les dates.** Décidé par l'utilisateur le 08/09/2026 : *« toutes les dates
 * vides, à remplir »*. Deux autres façons lui ont été proposées — décaler
 * toutes les étapes à partir d'une seule date saisie, ou garder celles de
 * l'original — et il a choisi la plus sûre en connaissance de cause : rien
 * n'est supposé, et `validerEtape` refuse d'enregistrer tant qu'une date
 * manque. Un ordre dupliqué ne peut donc PAS naître dans le passé.
 */
export function ordreDuplique(ordre: OrdreTransport): SaisieOrdre {
  return { ...ordreDepuis(ordre), numero: '' };
}

/**
 * Une étape recopiée, prête à être complétée.
 *
 * CE QUI DISPARAÎT, et pourquoi :
 *
 * - **le jour** — voir `ordreDuplique` ;
 * - **le camion, le chauffeur, la remorque** — ils appartiennent à un jour
 *   précis. L'API les repose toute seule à partir de l'attelage de la nouvelle
 *   date (`completerLEquipage`, vérifié dans `apps/api/src/ordres_transport.ts`),
 *   et les recopier ici poserait l'attelage d'il y a trois semaines ;
 * - **« fait » et sa date** — un transport à venir n'est pas déjà livré. Le
 *   recopier ferait naître l'ordre au statut « livré », puisque le statut se
 *   déduit des étapes ;
 * - **la référence de l'étape** (F2, 31/10/2026) — c'est le numéro d'un
 *   rendez-vous ou d'une commande précise : celui du transport recopié ne vaut
 *   pas pour le nouveau.
 *
 * CE QUI RESTE, et qui pourrait surprendre : **l'adresse figée du site**. Elle
 * est recopiée telle qu'elle a été employée, et non relue de la fiche. C'est le
 * sens même de la duplication — refaire le transport qu'on a fait —, et
 * l'adresse est sous les yeux dans le formulaire : elle se corrige si le client
 * a déménagé.
 */
export function etapeDupliquee(etape: Etape): SaisieEtape {
  return {
    ...etapeDepuis(etape),
    jour: '',
    vehicule_id: '',
    chauffeur_id: '',
    remorque_id: '',
    fait: false,
    arrive_jour: '',
    arrive_heure: '',
    reparti_jour: '',
    reparti_heure: '',
    reference: '',
  };
}

/**
 * Les étapes d'un ordre, recopiées DANS L'ORDRE DE LA ROUTE.
 *
 * Le tri par rang n'est pas une précaution de style : le formulaire de création
 * pose les étapes dans l'ordre où il les reçoit, et une livraison qui arriverait
 * avant son chargement se verrait attribuer le rang 1. L'API rangerait ensuite
 * le statut sur cette base.
 *
 * `[...etapes]` avant `sort` : `sort` trie sur place, et la liste vient des
 * propriétés React — la modifier ferait muter l'état affiché.
 */
export function etapesDupliquees(etapes: readonly Etape[]): readonly SaisieEtape[] {
  return [...etapes].sort((a, b) => a.rang - b.rang).map(etapeDupliquee);
}

export function validerEtape(saisie: SaisieEtape): Readonly<Record<string, string>> {
  const fautes: Record<string, string> = {};

  if (saisie.site_id.trim() === '') {
    fautes['site_id'] = 'Choisissez un site, ou créez-le.';
  }
  if (saisie.site_nom.trim() === '') {
    fautes['site_nom'] = 'Le nom du lieu est obligatoire.';
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(saisie.jour)) {
    fautes['jour'] = 'Indiquez la date de l’étape.';
  }

  const heure = /^\d{2}:\d{2}$/;
  if (saisie.heure_debut !== '' && !heure.test(saisie.heure_debut)) {
    fautes['heure_debut'] = 'Une heure, sous la forme 08:00.';
  }
  if (saisie.heure_fin !== '' && !heure.test(saisie.heure_fin)) {
    fautes['heure_fin'] = 'Une heure, sous la forme 09:30.';
  }
  // Le créneau qui finirait avant de commencer : la base le refuse aussi, par
  // `etapes_creneau_sense`, mais par une erreur brute que personne ne lit.
  if (
    heure.test(saisie.heure_debut) &&
    heure.test(saisie.heure_fin) &&
    saisie.heure_fin < saisie.heure_debut
  ) {
    fautes['heure_fin'] = 'La fin du créneau précède son début.';
  }

  const quantite = saisie.quantite.trim().replace(',', '.');
  if (quantite !== '' && !/^[0-9]{1,8}(\.[0-9]{1,2})?$/.test(quantite)) {
    fautes['quantite'] = 'Un nombre positif, deux décimales au plus.';
  }
  const poids = saisie.poids.trim();
  if (poids !== '' && !/^[0-9]{1,6}$/.test(poids)) {
    fautes['poids'] = 'Un nombre entier de kilogrammes.';
  }
  if (poids !== '' && Number(poids) > 100000) {
    fautes['poids'] = '100 000 kg au plus.';
  }

  // Cocher « fait » sans dire quand : accepté, l'API pose l'heure de la saisie.
  // Mais une heure sans jour, ou l'inverse, ne compose aucun instant.
  if (saisie.arrive_jour !== '' && saisie.arrive_heure === '') {
    fautes['arrive_heure'] = 'Indiquez aussi l’heure d’arrivée.';
  }
  if (saisie.arrive_heure !== '' && saisie.arrive_jour === '') {
    fautes['arrive_jour'] = 'Indiquez aussi le jour d’arrivée.';
  }
  // LE JOUR DU DÉPART SE DÉDUIT DE CELUI DE L'ARRIVÉE quand il est vide — donc
  // l'heure de départ seule suffit, à condition qu'un jour d'arrivée existe.
  if (
    saisie.reparti_heure !== '' &&
    saisie.reparti_jour === '' &&
    saisie.arrive_jour === ''
  ) {
    fautes['reparti_jour'] = 'Indiquez le jour du départ.';
  }
  if (saisie.reparti_jour !== '' && saisie.reparti_heure === '') {
    fautes['reparti_heure'] = 'Indiquez aussi l’heure de départ.';
  }

  // ON NE REPART PAS AVANT D'ÊTRE ARRIVÉ. La base le refuse par le contrôle
  // `etapes_heures_ordonnees` ; l'attraper ici évite à l'exploitant une erreur
  // brute de PostgreSQL sur une inversion de deux chiffres.
  const arriveeSaisie = instantDepuis(saisie.arrive_jour, saisie.arrive_heure);
  const departSaisi = instantDepuis(
    saisie.reparti_jour === '' ? saisie.arrive_jour : saisie.reparti_jour,
    saisie.reparti_heure,
  );
  if (
    arriveeSaisie !== null &&
    departSaisi !== null &&
    Date.parse(departSaisi) < Date.parse(arriveeSaisie)
  ) {
    fautes['reparti_heure'] = 'Le départ est avant l’arrivée.';
  }

  // LA RÉFÉRENCE DE L'ÉTAPE — F2. Même borne que l'API (120 caractères).
  if (saisie.reference.trim().length > 120) {
    fautes['reference'] = '120 caractères au plus.';
  }

  return fautes;
}

/**
 * Le corps envoyé pour une étape.
 *
 * `null` pour un champ vidé, absent pour un champ jamais renseigné : c'est la
 * même règle que partout. Les quatre lignes d'adresse partent TOUJOURS à la
 * création — l'API recopierait celles de la fiche du site, mais l'écran les a
 * peut-être modifiées.
 *
 * `arrive_le` et `reparti_le` sont COMPOSÉS de champs de l'écran, un jour et
 * une heure. Ils ne partent que si l'instant se compose ; sinon l'API pose
 * l'heure de la saisie sur le départ, ce qui est le comportement voulu quand
 * on coche sans préciser. Le jour du départ n'est pas saisi : il vaut celui de
 * l'arrivée, sauf à être donné explicitement pour une étape à cheval sur
 * minuit.
 */
export function corpsEtape(
  saisie: SaisieEtape,
  creation: boolean,
  /**
   * L'étape telle qu'elle était avant la saisie, pour les trois champs
   * d'équipage. `null` en création, ou quand l'appelant veut tout envoyer.
   */
  initiale: SaisieEtape | null = null,
): Readonly<Record<string, string | boolean | null>> {
  const corps: Record<string, string | boolean | null> = {};

  const poser = (cle: string, valeur: string) => {
    const propre = valeur.trim();
    if (creation) {
      if (propre !== '') corps[cle] = propre;
    } else {
      corps[cle] = propre === '' ? null : propre;
    }
  };

  if (creation) {
    corps['type'] = saisie.type;
  }
  corps['site_id'] = saisie.site_id.trim();
  corps['jour'] = saisie.jour;

  // L'ÉQUIPAGE est FACULTATIF et le reste : un ordre se saisit avant d'être
  // planifié. À la création on n'envoie que ce qui est choisi ; à la
  // modification, un champ vidé retire ce qu'il portait.
  //
  // Le chauffeur et la remorque ne sont envoyés QUE si l'écran en a un : les
  // laisser absents fait recopier l'attelage du jour par l'API — c'est le geste
  // ordinaire quand on pose un camion sur une étape qui n'en avait pas.
  for (const nom of ['vehicule_id', 'chauffeur_id', 'remorque_id'] as const) {
    const valeur = saisie[nom].trim();
    if (creation) {
      if (valeur !== '') corps[nom] = valeur;
      continue;
    }
    // ON N'ENVOIE QUE CE QUI A CHANGÉ, la règle du dépôt — et ici elle n'est
    // pas qu'une économie de mots. Poser un camion sur une étape qui n'avait
    // pas d'équipage n'envoie QUE le camion, et l'API recopie alors l'attelage
    // du jour. Envoyer « chauffeur : rien » l'en empêcherait, et la
    // planification ne remplirait jamais personne.
    if (initiale !== null && initiale[nom].trim() === valeur) continue;
    corps[nom] = valeur === '' ? null : valeur;
  }

  poser('site_nom', saisie.site_nom);
  poser('site_adresse', saisie.site_adresse);
  poser('site_code_postal', saisie.site_code_postal);
  poser('site_ville', saisie.site_ville);
  poser('heure_debut', saisie.heure_debut);
  poser('heure_fin', saisie.heure_fin);
  poser('quantite', saisie.quantite.replace(',', '.'));
  poser('support', saisie.support);
  poser('poids', saisie.poids);
  poser('reference', saisie.reference);
  poser('note', saisie.note);

  if (!creation) {
    corps['fait'] = saisie.fait;
    // LE JOUR DU DÉPART VAUT CELUI DE L'ARRIVÉE quand il n'est pas saisi.
    // Presque toutes les étapes se font dans la même journée ; faire retaper
    // la date serait une saisie de plus pour rien. Une étape à cheval sur
    // minuit — arrivée 23 h 40, repartie 00 h 20 — se règle en donnant
    // explicitement le jour du départ.
    const arriveLe = instantDepuis(saisie.arrive_jour, saisie.arrive_heure);
    const repartiLe = instantDepuis(
      saisie.reparti_jour === '' ? saisie.arrive_jour : saisie.reparti_jour,
      saisie.reparti_heure,
    );
    if (saisie.fait) {
      if (arriveLe !== null) corps['arrive_le'] = arriveLe;
      if (repartiLe !== null) corps['reparti_le'] = repartiLe;
    }
  }

  return corps;
}

// ---------------------------------------------------------------------------
// Valider une étape, ouvrir un litige
// ---------------------------------------------------------------------------

/**
 * Ce qu'on saisit pour VALIDER une étape.
 *
 * « Valider », et non « c'est fait » — mot choisi par l'utilisateur le
 * 04/09/2026. Et **la date de réalisation se demande au moment où l'on
 * valide**, pas dans la fiche de l'ordre : c'est là qu'on la connaît, et la
 * chercher ailleurs obligerait à ouvrir un formulaire pour un geste qui doit
 * en être un seul.
 */
export interface SaisieValidation {
  readonly etapeId: string;
  readonly jour: string;
  /**
   * L'HEURE D'ARRIVÉE EST FACULTATIVE, celle de DÉPART obligatoire.
   *
   * Défaut signalé par l'utilisateur le 09/09/2026 : la boîte de validation ne
   * demandait qu'une heure alors que le formulaire d'étape en demandait deux.
   * Le geste du quotidien passe par ICI, pas par « Modifier ».
   *
   * Pourquoi l'arrivée n'est pas obligatoire : on valide souvent le lendemain
   * matin les livraisons de la veille, et l'exploitant connaît l'heure à
   * laquelle le camion est reparti sans toujours savoir celle où il s'est
   * garé. La rendre obligatoire bloquerait la validation — donc le statut de
   * l'ordre, donc la facturation — pour une donnée de confort. Elle est
   * demandée, pas exigée.
   */
  readonly heureArrivee: string;
  readonly heureDepart: string;
}

/** Le refus à afficher sous les champs, ou `null` si la saisie tient. */
export function refusValidation(saisie: {
  readonly jour: string;
  readonly heureArrivee: string;
  readonly heureDepart: string;
}): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(saisie.jour)) {
    return 'Indiquez le jour de la réalisation.';
  }
  if (!/^\d{2}:\d{2}/.test(saisie.heureDepart)) {
    return 'Indiquez l’heure de départ.';
  }
  const depart = instantDepuis(saisie.jour, saisie.heureDepart);
  if (depart === null) {
    return 'Cette date et cette heure n’existent pas.';
  }
  if (saisie.heureArrivee !== '') {
    const arrivee = instantDepuis(saisie.jour, saisie.heureArrivee);
    if (arrivee === null) {
      return 'Cette heure d’arrivée n’existe pas.';
    }
    // ON NE REPART PAS AVANT D'ÊTRE ARRIVÉ. La base le refuse par le contrôle
    // `etapes_heures_ordonnees` ; l'attraper ici évite une erreur brute de
    // PostgreSQL sur une inversion de deux chiffres.
    if (Date.parse(depart) < Date.parse(arrivee)) {
      return 'Le départ est avant l’arrivée.';
    }
  }
  return null;
}

/**
 * Ce qu'on saisit pour ouvrir un LITIGE.
 *
 * `motifNouveau` porte le motif qu'on crée à la volée. **Sans lui, l'écran ne
 * servait à rien le premier jour** : la table `motifs_litige` naît vide — la
 * migration n'y insère aucune ligne — et aucun autre écran n'en crée. Constaté
 * par l'utilisateur le 04/09/2026 : « Litige ne marche pas ». C'est le même
 * défaut que celui évité sur les absences le 03/09, et refait ici.
 */
export interface SaisieLitige {
  readonly etapeId: string;
  readonly motifId: string;
  /** Non nul quand on est en train de créer un motif plutôt que d'en choisir un. */
  readonly motifNouveau: string | null;
  readonly note: string;
  readonly jour: string;
  readonly heure: string;
}

export function refusLitige(saisie: {
  readonly motifId: string;
  readonly motifNouveau: string | null;
  readonly jour: string;
  readonly heure: string;
}): string | null {
  if (saisie.motifNouveau !== null) {
    if (saisie.motifNouveau.trim() === '') {
      return 'Donnez un nom au motif.';
    }
    if (saisie.motifNouveau.trim().length > 120) {
      return '120 caractères au plus.';
    }
  } else if (saisie.motifId === '') {
    return 'Choisissez un motif, ou créez-en un.';
  }

  // Le constat peut n'être pas daté : l'API pose alors l'instant de la saisie.
  // Mais un jour sans heure, ou l'inverse, ne compose aucun instant.
  if (saisie.jour !== '' && saisie.heure === '') {
    return 'Indiquez aussi l’heure du constat.';
  }
  if (saisie.heure !== '' && saisie.jour === '') {
    return 'Indiquez aussi le jour du constat.';
  }
  return null;
}

// ---------------------------------------------------------------------------
// La création d'un site sans quitter l'ordre
// ---------------------------------------------------------------------------

/**
 * LE PAYS REMPLACE LE CODE — F2, refonte de la saisie d'OT (31/10/2026).
 *
 * Mohamed, le 31/10 : « ajoute pays, et code on le laisse générer auto, on ne
 * l'a jamais fait à la main ». Le code n'est donc plus saisi : il est proposé
 * par `codePropose` au moment de créer, comme dans l'annuaire. Le pays part
 * sur « France », valeur par défaut de `sites.pays` (migration
 * 20260903190000_annuaire_sites.sql).
 */
export interface SaisieSite {
  readonly nom: string;
  readonly adresse: string;
  readonly code_postal: string;
  readonly ville: string;
  readonly pays: string;
}

export const SITE_VIDE: SaisieSite = {
  nom: '',
  adresse: '',
  code_postal: '',
  ville: '',
  pays: 'France',
};

/**
 * SEUL LE NOM EST OBLIGATOIRE — vérifié dans `apps/web/src/sites.ts`, où il est
 * le seul champ marqué `obligatoire`. Le téléphone, le courriel, la note et les
 * contacts se complètent plus tard depuis l'annuaire : les demander ici
 * retarderait la saisie de l'ordre.
 */
export function validerSite(saisie: SaisieSite): Readonly<Record<string, string>> {
  const fautes: Record<string, string> = {};
  if (saisie.nom.trim() === '') {
    fautes['nom'] = 'Le nom du site est obligatoire.';
  }
  if (saisie.nom.trim().length > 200) {
    fautes['nom'] = '200 caractères au plus.';
  }
  if (saisie.code_postal.trim().length > 16) {
    fautes['code_postal'] = '16 caractères au plus.';
  }
  return fautes;
}

export function pourCreationSite(
  saisie: SaisieSite,
  /**
   * D'OÙ VIENT LA FICHE — ajouté le 26/10/2026, lot 124.
   *
   * FACULTATIF, ET C'EST TOUT L'INTÉRÊT. Un site créé à la main depuis la
   * saisie d'un ordre n'a aucune provenance à déclarer : c'est quelqu'un qui
   * l'a tapé, et la colonne reste vide. Seule la création issue d'une
   * confirmation lue passe « Confirmation », parce qu'elle seule sait d'où
   * elle vient.
   *
   * LE JOUR OÙ UNE ADRESSE DE LIVRAISON SE RÉVÉLERA FAUSSE, la première
   * question sera : qui l'a saisie ? Sans cette valeur, personne ne pourra
   * répondre — et le skill `cargo-tms-saisie-ot` prévient depuis des mois que
   * « l'IA fait des erreurs sur n'importe quel champ ».
   */
  source?: string,
  /**
   * LE CODE ENGENDRÉ — F2, 31/10/2026. Calculé par l'appelant avec
   * `codePropose`, parce que c'est lui qui a la liste des sites déjà connus.
   * Vide : aucun code n'est envoyé, et la colonne nullable reste vide.
   */
  code?: string,
): Readonly<Record<string, string>> {
  const corps: Record<string, string> = { nom: saisie.nom.trim() };
  for (const cle of ['adresse', 'code_postal', 'ville', 'pays'] as const) {
    const valeur = saisie[cle].trim();
    if (valeur !== '') corps[cle] = valeur;
  }
  if (code !== undefined && code.trim() !== '') corps['code'] = code.trim();
  // VIDE N'EST PAS ABSENT : une source vide ne doit pas écrire une chaîne
  // vide en base, qui se lirait « on a renseigné que ça venait de nulle part ».
  if (source !== undefined && source.trim() !== '') corps['source'] = source.trim();
  return corps;
}

// ---------------------------------------------------------------------------
// Le mètre linéaire, pendant qu'on saisit
// ---------------------------------------------------------------------------

/**
 * Ce que l'écran écrit sous le support pendant la saisie.
 *
 * Demandé mot pour mot par l'utilisateur le 05/09/2026 : « lorsque
 * l'utilisateur met 10 palettes 80×120, l'app doit calculer automatiquement le
 * ML correspondant et le mètre à coter (total) ». Le total DOIT se voir avant
 * d'enregistrer, sans quoi on ne saurait qu'après coup ce qu'on va facturer.
 *
 * **C'est le même calcul que celui de l'API**, `packages/core`, et non un
 * second exemplaire : ce que l'écran annonce est exactement ce qui sera figé
 * sur l'étape. Deux calculs finiraient par ne plus dire la même chose, et
 * l'écart se verrait sur une facture.
 *
 * **10 palettes valent 4,00 ML**, la convention du métier — pas les 4,80 m de
 * plancher que quatre rangs bloquent réellement. Tranché par l'utilisateur :
 * « c'est 4ml ».
 *
 * Rend `null` quand il n'y a rien à dire : pas de support, ou un support sans
 * dimensions. **On se tait plutôt que d'écrire zéro** — un « 0,00 ML » se
 * lirait « n'occupe rien », ce qui est une réponse alors qu'on n'en a aucune.
 */
export function metreLineaireDit(
  support: string,
  quantite: string,
  supports: readonly {
    readonly nom: string;
    readonly longueur_cm?: number | null;
    readonly largeur_cm?: number | null;
    readonly hauteur_cm?: number | null;
    readonly etages?: number;
  }[],
): string | null {
  if (support.trim() === '') return null;

  const trouve = supports.find(
    (s) => s.nom.toLowerCase() === support.trim().toLowerCase(),
  );
  if (trouve === undefined) return null;

  const unitaire = metresLineaires({
    nom: trouve.nom,
    abrege: null,
    longueur_cm: trouve.longueur_cm ?? null,
    largeur_cm: trouve.largeur_cm ?? null,
    hauteur_cm: trouve.hauteur_cm ?? null,
    etages: trouve.etages ?? 1,
  });
  if (unitaire === null) return null;

  // La quantité se saisit à la virgule comme au point : le champ est un texte
  // libre tant qu'il n'est pas envoyé, et l'utilisateur tape ce qu'il veut.
  const nombre = Number(quantite.trim().replace(',', '.'));
  if (quantite.trim() === '' || !Number.isFinite(nombre) || nombre <= 0) {
    return `${mlLisible(unitaire)} ML l’unité`;
  }

  return `${mlLisible(unitaire)} ML l’unité · ${mlLisible(unitaire * nombre)} ML au total`;
}

/**
 * UNE PRESTATION ANNEXE SAISIE AVANT L'ENREGISTREMENT — F3, 01/11/2026.
 *
 * Maquette validée le 31/10 : sur l'ordre neuf, les prestations se posent en
 * lignes (Nature · Détail · Quantité · Prix unitaire), ajoutables et
 * retirables. Elles n'existent pas encore en base : elles partent par la
 * route des prestations (`ajouterUnePrestation`) juste après la création de
 * l'ordre. Mêmes noms de champs que la boîte « Prestation annexe ».
 */
export interface PrestationNeuve {
  readonly natureId: string;
  readonly detail: string;
  readonly quantite: string;
  readonly prixUnitaire: string;
}

/**
 * LE TOTAL HT DE LA SAISIE — prix + indexation + Σ quantité × prix unitaire.
 *
 * EN CENTIMES ENTIERS : additionner des euros en JavaScript, c'est additionner
 * des flottants binaires, et « 0,1 + 0,2 » n'y vaut pas « 0,3 ». Chaque ligne
 * est arrondie au centime, comme la route des prestations le fait en base.
 *
 * `null` si le prix n'est pas lisible : un total sans prix serait faux. Une
 * ligne de prestation illisible est ignorée — sa faute se voit dans sa case.
 */
export function totalHtDeLaSaisie(
  prix: string,
  indexation: string | null,
  prestations: readonly PrestationNeuve[],
): string | null {
  const centimes = (texte: string): number | null => {
    const n = Number(texte.trim().replace(',', '.'));
    return texte.trim() === '' || !Number.isFinite(n) ? null : Math.round(n * 100);
  };
  const base = centimes(prix);
  if (base === null) return null;
  let total = base + (indexation === null ? 0 : (centimes(indexation) ?? 0));
  for (const p of prestations) {
    const q = Number(p.quantite.trim().replace(',', '.'));
    const pu = Number(p.prixUnitaire.trim().replace(',', '.'));
    if (p.quantite.trim() === '' || p.prixUnitaire.trim() === '') continue;
    if (!Number.isFinite(q) || !Number.isFinite(pu)) continue;
    total += Math.round(q * pu * 100);
  }
  return (total / 100).toFixed(2);
}

// ---------------------------------------------------------------------------
// LES LIGNES DE MARCHANDISE DU PACKAGING — F4, 01/11/2026
// ---------------------------------------------------------------------------

/** Un support tel que l'écran le reçoit, avec ses dimensions. */
type SupportDimensionne = {
  readonly nom: string;
  readonly longueur_cm?: number | null;
  readonly largeur_cm?: number | null;
  readonly hauteur_cm?: number | null;
  readonly etages?: number;
};

/**
 * Le choix « Autre dimension » du menu des supports. Ce n'est pas un support
 * des Paramètres : il ouvre les trois cases de dimension à la saisie (cadrage
 * §7). C'est ce libellé qui est écrit dans `support`.
 */
export const AUTRE_DIMENSION = 'Autre dimension';

/**
 * Une ligne du Packaging pendant la saisie. Cadrage §7, validé le 31/10 :
 * Support · Quantité · Longueur · Largeur · Hauteur · Poids · Palettes
 * consignées, et « Gerbable par N ».
 *
 * `chargement` et `livraison` sont les RANGS des étapes dans la saisie (0, 1,
 * 2…) : les étapes n'ont pas encore d'identifiant avant l'enregistrement.
 */
export interface LigneMarchandise {
  readonly chargement: number;
  readonly livraison: number;
  readonly support: string;
  readonly quantite: string;
  readonly poids: string;
  readonly longueur: string;
  readonly largeur: string;
  readonly hauteur: string;
  readonly gerbable: boolean;
  /** « par N » : le nombre de niveaux, 2 au moins quand c'est gerbable. */
  readonly etages: string;
  readonly consigne: boolean;
}

/** Les rangs du premier chargement et de la première livraison qui le suit. */
function bornesParDefaut(types: readonly string[]): { chargement: number; livraison: number } {
  const chargement = Math.max(0, types.indexOf('chargement'));
  const apres = types.findIndex((t, i) => i > chargement && t === 'livraison');
  const livraison = apres >= 0 ? apres : Math.max(0, types.indexOf('livraison'));
  return { chargement, livraison };
}

/**
 * Une ligne neuve : affectée d'office au premier chargement et à la livraison
 * qui le suit — avec un seul chargement et une seule livraison, rien d'autre
 * n'est à dire (cadrage §7, « affectation d'office »).
 */
export function ligneMarchandiseVide(types: readonly string[]): LigneMarchandise {
  return {
    ...bornesParDefaut(types),
    support: '',
    quantite: '',
    poids: '',
    longueur: '',
    largeur: '',
    hauteur: '',
    gerbable: false,
    etages: '2',
    consigne: false,
  };
}

/**
 * CHOISIR UN SUPPORT PRÉREMPLIT SES DIMENSIONS ET SON GERBAGE (cadrage §7) :
 * longueur et largeur viennent des Paramètres et ne se modifient pas, la
 * hauteur reste modifiable, « Gerbable par N » part de `supports.etages`.
 * « Autre dimension » vide les trois cases pour la saisie à la main.
 */
export function choisirLeSupport(
  ligne: LigneMarchandise,
  nom: string,
  supports: readonly SupportDimensionne[],
): LigneMarchandise {
  if (nom === AUTRE_DIMENSION) {
    return { ...ligne, support: nom, longueur: '', largeur: '', hauteur: '' };
  }
  const trouve = supports.find((s) => s.nom === nom);
  const dim = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v));
  const etages = trouve?.etages ?? 1;
  return {
    ...ligne,
    support: nom,
    longueur: dim(trouve?.longueur_cm),
    largeur: dim(trouve?.largeur_cm),
    hauteur: dim(trouve?.hauteur_cm),
    gerbable: etages > 1,
    etages: etages > 1 ? String(etages) : '2',
  };
}

/** Les niveaux qui entrent dans le calcul : 1 si la ligne n'est pas gerbable. */
function niveaux(ligne: LigneMarchandise): number {
  if (!ligne.gerbable) return 1;
  const n = Number(ligne.etages.trim());
  return Number.isInteger(n) && n >= 1 && n <= 10 ? n : 1;
}

/** Un entier positif lu dans une case, ou `null`. */
function centimetres(texte: string): number | null {
  const n = Number(texte.trim().replace(',', '.'));
  return texte.trim() === '' || !Number.isFinite(n) || n <= 0 ? null : Math.round(n);
}

/**
 * Le métrage d'UNE unité de la ligne, par le calcul de l'API (`packages/core`,
 * règle de la surface du 31/10) : ce que l'écran annonce est ce qui sera figé.
 * `null` sans dimensions — jamais zéro.
 */
export function mlUnitaireDeLaLigne(ligne: LigneMarchandise): number | null {
  return metresLineaires({
    nom: ligne.support,
    abrege: null,
    longueur_cm: centimetres(ligne.longueur),
    largeur_cm: centimetres(ligne.largeur),
    hauteur_cm: null,
    etages: niveaux(ligne),
  });
}

/**
 * Le métrage linéaire total et le poids total du Packaging. Même règle que
 * `metreLineaireDit` : `null` plutôt que zéro quand rien ne se calcule.
 */
export function totauxDesMarchandises(
  lignes: readonly LigneMarchandise[],
): { readonly ml: number | null; readonly poids: number | null } {
  let ml: number | null = null;
  let poids: number | null = null;
  for (const l of lignes) {
    const q = Number(l.quantite.trim().replace(',', '.'));
    const u = mlUnitaireDeLaLigne(l);
    if (u !== null && l.quantite.trim() !== '' && Number.isFinite(q) && q > 0) {
      ml = (ml ?? 0) + u * q;
    }
    const kg = Number(l.poids.trim());
    if (l.poids.trim() !== '' && Number.isFinite(kg) && kg >= 0) poids = (poids ?? 0) + kg;
  }
  return { ml, poids };
}

/**
 * LES LIGNES QUE PORTENT DÉJÀ LES ÉTAPES — après la lecture d'une
 * confirmation, ou la duplication d'un ordre : la marchandise est sur les
 * chargements (`quantite`, `support`, `poids`). Une ligne par chargement qui
 * en porte, livrée à la première livraison qui le suit.
 */
export function marchandisesDepuisEtapes(
  etapes: readonly SaisieEtape[],
  supports: readonly SupportDimensionne[],
): readonly LigneMarchandise[] {
  const types = etapes.map((e) => e.type);
  const lignes: LigneMarchandise[] = [];
  etapes.forEach((e, rang) => {
    if (e.type !== 'chargement') return;
    if (e.quantite.trim() === '' && e.support.trim() === '' && e.poids.trim() === '') return;
    const apres = types.findIndex((t, i) => i > rang && t === 'livraison');
    const base = choisirLeSupport(
      { ...ligneMarchandiseVide(types), chargement: rang, livraison: apres >= 0 ? apres : bornesParDefaut(types).livraison },
      e.support,
      supports,
    );
    lignes.push({ ...base, support: e.support, quantite: e.quantite, poids: e.poids });
  });
  return lignes;
}

/** Les fautes d'une ligne, par champ. */
export function validerLigneMarchandise(
  ligne: LigneMarchandise,
  types: readonly string[],
): Readonly<Record<string, string>> {
  const fautes: Record<string, string> = {};
  if (types[ligne.chargement] !== 'chargement') fautes['chargement'] = 'Choisissez le chargement.';
  if (types[ligne.livraison] !== 'livraison') fautes['livraison'] = 'Choisissez la livraison.';
  const q = Number(ligne.quantite.trim().replace(',', '.'));
  if (ligne.quantite.trim() !== '' && (!Number.isFinite(q) || q < 0)) {
    fautes['quantite'] = 'Un nombre positif.';
  }
  const kg = Number(ligne.poids.trim());
  if (ligne.poids.trim() !== '' && (!Number.isInteger(kg) || kg < 0)) {
    fautes['poids'] = 'Des kilos, sans virgule.';
  }
  for (const cle of ['longueur', 'largeur', 'hauteur'] as const) {
    if (ligne[cle].trim() !== '' && centimetres(ligne[cle]) === null) {
      fautes[cle] = 'Des centimètres.';
    }
  }
  if (ligne.gerbable) {
    const n = Number(ligne.etages.trim());
    if (!Number.isInteger(n) || n < 2 || n > 10) fautes['etages'] = 'Entre 2 et 10.';
  }
  return fautes;
}

/**
 * Le corps de `PUT /ordres-transport/:id/marchandises`. `ids` donne
 * l'identifiant de chaque étape par son rang, une fois les étapes créées.
 * Le métrage ne part pas : l'API le calcule et le fige.
 */
export function corpsMarchandises(
  lignes: readonly LigneMarchandise[],
  ids: readonly string[],
): readonly Record<string, string | number | boolean | null>[] {
  return lignes.map((l) => {
    const q = l.quantite.trim().replace(',', '.');
    const kg = l.poids.trim();
    return {
      etape_chargement_id: ids[l.chargement] ?? '',
      etape_livraison_id: ids[l.livraison] ?? '',
      support: l.support.trim() === '' ? null : l.support.trim(),
      quantite: q === '' ? null : q,
      poids: kg === '' ? null : Number(kg),
      longueur_cm: centimetres(l.longueur),
      largeur_cm: centimetres(l.largeur),
      hauteur_cm: centimetres(l.hauteur),
      etages: niveaux(l),
      consigne: l.consigne,
    };
  });
}

/**
 * LES LIGNES ENREGISTRÉES, REMISES EN SAISIE — pour modifier le Packaging
 * d'un ordre existant. Les étapes y pointent par identifiant ; la saisie, par
 * rang. Une dimension enregistrée se garde telle quelle : c'est elle qui a
 * donné le métrage figé.
 */
export function lignesDepuisLues(
  lues: readonly MarchandiseLue[],
  etapes: readonly { readonly id: string; readonly type: string }[],
): readonly LigneMarchandise[] {
  const types = etapes.map((e) => e.type);
  const defaut = ligneMarchandiseVide(types);
  const rangDe = (id: string, repli: number) => {
    const i = etapes.findIndex((e) => e.id === id);
    return i >= 0 ? i : repli;
  };
  const texte = (v: number | null) => (v === null ? '' : String(v));
  return lues.map((m) => ({
    chargement: rangDe(m.etape_chargement_id, defaut.chargement),
    livraison: rangDe(m.etape_livraison_id, defaut.livraison),
    support: m.support ?? '',
    quantite: m.quantite === null ? '' : String(Number(m.quantite)),
    poids: texte(m.poids),
    longueur: texte(m.longueur_cm),
    largeur: texte(m.largeur_cm),
    hauteur: texte(m.hauteur_cm),
    gerbable: m.etages > 1,
    etages: m.etages > 1 ? String(m.etages) : '2',
    consigne: m.consigne,
  }));
}

/** Une ligne enregistrée, telle que l'API la rend avec la fiche. */
export interface MarchandiseLue {
  readonly id: string;
  readonly rang: number;
  readonly etape_chargement_id: string;
  readonly etape_livraison_id: string;
  readonly support: string | null;
  readonly quantite: string | null;
  readonly poids: number | null;
  readonly longueur_cm: number | null;
  readonly largeur_cm: number | null;
  readonly hauteur_cm: number | null;
  readonly etages: number;
  readonly ml_unitaire: string | null;
  readonly consigne: boolean;
}

// ---------------------------------------------------------------------------
// L'échange de palettes, saisi sur l'étape
// ---------------------------------------------------------------------------

/**
 * Ce que la boîte « Échange » demande.
 *
 * DEUX CHIFFRES, ET DEUX SEULEMENT. « Restées » est livrées − reprises : le
 * saisir serait un troisième nombre à tenir d'accord, et c'est exactement ce
 * que la v1 faisait, avec `restees` ET `ecart` en plus de la quantité.
 */
export interface SaisieEchange {
  readonly support: string;
  readonly livrees: string;
  readonly reprises: string;
  readonly bonNumero: string;
  readonly bonExpire: string;
}

/**
 * L'échange pré-rempli depuis l'étape.
 *
 * « Livrées » prend la quantité de l'étape QUAND IL N'Y A PAS ENCORE
 * D'ÉCHANGE, sans y être lié : le cadrage prévoit que la quantité échangée
 * puisse différer de la quantité transportée. C'est une proposition, pas une
 * copie.
 */
export function echangeDepuis(etape: Etape): SaisieEchange {
  if (etape.echange_support !== null) {
    return {
      support: etape.echange_support,
      livrees: String(etape.echange_livrees ?? 0),
      reprises: String(etape.echange_reprises ?? 0),
      bonNumero: etape.echange_bon_numero ?? '',
      bonExpire: '',
    };
  }
  return {
    support: etape.support ?? '',
    livrees: etape.quantite === null ? '' : String(Math.round(Number(etape.quantite))),
    reprises: '',
    bonNumero: '',
    bonExpire: '',
  };
}

function entierOuRien(mot: string): number | null {
  const propre = mot.trim();
  if (propre === '') return 0;
  if (!/^\d{1,5}$/.test(propre)) return null;
  return Number(propre);
}

/**
 * Ce qui empêche d'enregistrer, en français. `null` quand tout va.
 *
 * Les mêmes refus que l'API, dits ici pour que la boîte n'ait pas besoin d'un
 * aller-retour pour les afficher. C'est le doublement habituel : l'écran
 * explique, la base garantit.
 */
export function refusEchange(saisie: SaisieEchange): string | null {
  if (saisie.support.trim() === '') {
    return 'Choisissez le support échangé.';
  }

  const livrees = entierOuRien(saisie.livrees);
  const reprises = entierOuRien(saisie.reprises);
  if (livrees === null || reprises === null) {
    return 'Les palettes se comptent en nombre entier.';
  }
  if (livrees === 0 && reprises === 0) {
    return 'Un échange sans palette livrée ni reprise n’est pas un échange.';
  }

  if (saisie.bonNumero.trim() !== '' && livrees - reprises <= 0) {
    // Un bon est une reconnaissance de dette EN NOTRE FAVEUR : il n'a pas de
    // sens quand c'est nous qui devons.
    return 'Un bon ne se justifie que si le client nous doit des palettes.';
  }

  return null;
}

/** L'écart de l'échange en cours de saisie, ou `null` s'il ne se calcule pas. */
export function ecartEchange(saisie: SaisieEchange): number | null {
  const livrees = entierOuRien(saisie.livrees);
  const reprises = entierOuRien(saisie.reprises);
  if (livrees === null || reprises === null) return null;
  return livrees - reprises;
}

/** Ce que la route `PUT /emballage/etapes/:id` attend. */
export function corpsEchange(
  saisie: SaisieEchange,
): Readonly<Record<string, string | number>> {
  const bon = saisie.bonNumero.trim();
  const expire = saisie.bonExpire.trim();
  return {
    support: saisie.support,
    livrees: entierOuRien(saisie.livrees) ?? 0,
    reprises: entierOuRien(saisie.reprises) ?? 0,
    ...(bon === '' ? {} : { bon_numero: bon }),
    ...(bon === '' || expire === '' ? {} : { bon_expire_le: expire }),
  };
}

// ---------------------------------------------------------------------------
// L'affrètement, vu de la liste
// ---------------------------------------------------------------------------

/**
 * CET ORDRE EST-IL AFFRÉTÉ ?
 *
 * `affretement_id` et lui seul. `affrete_par` peut être présent sans que
 * l'ordre le soit — non, il ne le peut pas : l'API les rend par la même
 * jointure, sur l'affrètement NON ANNULÉ
 * (`apps/api/src/ordres_transport.ts`, `left join affretements af on
 * af.ot_id = o.id and af.annule_le is null`). Mais c'est l'identifiant qui
 * porte le fait ; le nom n'est qu'une commodité d'affichage, et un nom vide ne
 * doit pas faire disparaître la pastille.
 */
export function estAffrete(ordre: { readonly affretement_id: string | null }): boolean {
  return ordre.affretement_id !== null;
}

/**
 * L'INFOBULLE de la pastille : « Affrété par DUMONT · achat 1 940,00 € ».
 *
 * Rend `null` si l'ordre n'est pas affrété — il n'y a alors pas de pastille à
 * survoler. Le nom manquant ne fait pas mentir la phrase : on écrit « Affrété »
 * tout court plutôt que « Affrété par null ».
 *
 * Le prix d'achat y figure parce que c'est la question suivante — combien —, et
 * qu'il n'a pas sa place dans une colonne du tableau : la vente y est déjà, les
 * deux côte à côte inviteraient à lire une marge que la liste ne calcule pas.
 */
export function affreteDit(ordre: {
  readonly affretement_id: string | null;
  readonly affrete_par: string | null;
  readonly prix_achat: string | null;
}): string | null {
  if (!estAffrete(ordre)) return null;
  const qui =
    ordre.affrete_par === null || ordre.affrete_par.trim() === ''
      ? 'Affrété'
      : `Affrété par ${ordre.affrete_par}`;
  const combien = enEuros(ordre.prix_achat);
  return combien === null ? qui : `${qui} · achat ${combien}`;
}

// ===========================================================================
// LE TRI PAR EN-TÊTE — validé le 10/09/2026
// ===========================================================================

/**
 * Les colonnes triables. Ce sont les huit du tableau, et rien d'autre : une
 * clé qui ne correspond à aucun en-tête serait un tri que personne ne peut
 * demander ni annuler.
 */
export type ColonneTri =
  | 'numero'
  | 'client'
  | 'chargement'
  | 'chargement_lieu'
  | 'livraison'
  | 'livraison_lieu'
  | 'statut'
  | 'vente';

export type SensTri = 'croissant' | 'decroissant';

export interface Tri {
  readonly colonne: ColonneTri;
  readonly sens: SensTri;
}

/**
 * LE TRI PAR DÉFAUT : le numéro décroissant, c'est-à-dire du plus récent au
 * plus ancien. C'est ce que faisait `ranger()` avant qu'on puisse trier, et
 * l'écran y revient quand on reclique sur « N° d'OT ».
 */
export const TRI_DEFAUT: Tri = { colonne: 'numero', sens: 'decroissant' };

/**
 * LE PROCHAIN TRI quand on clique sur un en-tête.
 *
 * Deux états seulement — croissant, décroissant — et non trois. Un troisième
 * état « pas de tri » oblige à cliquer trois fois pour revenir en arrière, et
 * personne ne devine où il en est. Pour retrouver l'ordre par défaut, on
 * clique sur « N° d'OT ».
 *
 * UNE COLONNE NEUVE PART EN CROISSANT, sauf les deux où le décroissant est ce
 * qu'on veut voir d'abord : la vente (les plus gros d'abord) et le numéro (le
 * plus récent d'abord).
 */
export function triSuivant(actuel: Tri, colonne: ColonneTri): Tri {
  if (actuel.colonne === colonne) {
    return {
      colonne,
      sens: actuel.sens === 'croissant' ? 'decroissant' : 'croissant',
    };
  }
  const dabordDecroissant = colonne === 'vente' || colonne === 'numero';
  return { colonne, sens: dabordDecroissant ? 'decroissant' : 'croissant' };
}

/** Un nombre depuis un `numeric` rendu en texte. `null` quand il n'y en a pas. */
function nombreOuNul(valeur: string | null): number | null {
  if (valeur === null || valeur.trim() === '') return null;
  const lu = Number(valeur.replace(/\s/gu, '').replace(',', '.'));
  return Number.isFinite(lu) ? lu : null;
}

/**
 * CE SUR QUOI UNE COLONNE SE TRIE.
 *
 * Rend `null` quand la case est vide — c'est ce `null` qui envoie la ligne en
 * dernier, dans les deux sens.
 *
 * LE STATUT SE TRIE DANS L'ORDRE DU MÉTIER, pas dans l'ordre alphabétique :
 * on rend son RANG dans `STATUTS`, qui va d'`attente` à `annule`. Par ordre
 * alphabétique, « Annulé » arriverait en tête, ce qui ne veut rien dire.
 *
 * LA VENTE SE TRIE EN NOMBRE. En texte, « 960,00 » passerait après
 * « 4 190,00 » — c'est le genre d'erreur qu'on ne voit pas tout de suite et
 * qui fait douter de tout le tableau.
 */
function clefDeTri(ordre: OrdreTransport, colonne: ColonneTri): string | number | null {
  switch (colonne) {
    case 'numero':
      return ordre.numero;
    case 'client':
      return ordre.client;
    case 'chargement':
      return ordre.chargement_jour;
    case 'chargement_lieu':
      return ordre.chargement_site ?? ordre.chargement_ville;
    case 'livraison':
      return ordre.livraison_jour;
    case 'livraison_lieu':
      return ordre.livraison_site ?? ordre.livraison_ville;
    case 'statut': {
      const rang = STATUTS.findIndex((s) => s.cle === ordre.statut);
      // Un statut inconnu — une valeur ajoutée en base et pas encore ici — va
      // en dernier plutôt que de se ranger n'importe où.
      return rang === -1 ? null : rang;
    }
    case 'vente':
      return nombreOuNul(ordre.prix_vente);
  }
}

/** `''` compte comme vide : une chaîne blanche n'est pas une valeur à trier. */
function estVide(clef: string | number | null): boolean {
  return clef === null || (typeof clef === 'string' && clef.trim() === '');
}

/**
 * TRIE LA LISTE DÉJÀ CHARGÉE.
 *
 * LES VIDES VONT TOUJOURS EN DERNIER, dans les deux sens — décision du
 * 10/09/2026. Un ordre sans étape n'a ni date ni lieu : en tri croissant il
 * passerait devant tout le monde et cacherait ce qu'on cherche.
 *
 * À ÉGALITÉ, LE NUMÉRO DÉCROISSANT DÉPARTAGE. Sans cela, deux ordres du même
 * jour changeraient de place d'un affichage à l'autre selon l'ordre où l'API
 * les a rendus, et l'œil ne pourrait pas suivre une ligne.
 */
export function trier(
  ordres: readonly OrdreTransport[],
  tri: Tri,
): readonly OrdreTransport[] {
  const signe = tri.sens === 'croissant' ? 1 : -1;

  return [...ordres].sort((a, b) => {
    const ca = clefDeTri(a, tri.colonne);
    const cb = clefDeTri(b, tri.colonne);

    const va = estVide(ca);
    const vb = estVide(cb);
    if (va !== vb) return va ? 1 : -1; // le vide en dernier, quel que soit le sens
    if (!va) {
      if (typeof ca === 'number' && typeof cb === 'number') {
        if (ca !== cb) return (ca - cb) * signe;
      } else {
        const ecart = String(ca).localeCompare(String(cb), 'fr', {
          numeric: true,
          sensitivity: 'base',
        });
        if (ecart !== 0) return ecart * signe;
      }
    }
    return b.numero.localeCompare(a.numero, 'fr');
  });
}

// ===========================================================================
// LES FILTRES — validés le 10/09/2026
// ===========================================================================

/**
 * ET ENTRE LES CRITÈRES, OU À L'INTÉRIEUR D'UN CRITÈRE.
 *
 * C'est la décision du 10/09, prise contre le « ou » libre que la demande
 * appelait littéralement. `A ET B OU C` ne veut rien dire tant qu'on n'a pas
 * posé les parenthèses, et un écran à parenthèses est un petit langage de
 * programmation — ça se conçoit pour un rapport, pas pour un tableau qu'on
 * ouvre trente fois par jour.
 *
 * Ici : « COPAL ou VASSEUR » ET « Planifié ou En cours ». Aucune ambiguïté
 * possible, et c'est ce que font les filtres d'un tableur.
 */

/** Un critère à cases à cocher. Les valeurs cochées sont en OU entre elles. */
export interface CritereFiltre {
  readonly cle: string;
  readonly nom: string;
  /**
   * Les valeurs que porte CET ordre pour ce critère. Une liste vide veut dire
   * que l'ordre ne correspond à aucune case : il sort dès que le critère est
   * posé, ce qui est bien ce qu'on attend.
   */
  readonly valeursDe: (ordre: OrdreTransport) => readonly string[];
  /**
   * Les cases offertes. `null` veut dire « à déduire de la liste chargée » —
   * les clients et les activités changent d'une société à l'autre, et une
   * liste figée serait fausse dès le premier client créé.
   */
  readonly choix: readonly { readonly valeur: string; readonly texte: string }[] | null;
}

export const CRITERES_FILTRE: readonly CritereFiltre[] = [
  /**
   * PAS DE CRITÈRE « CLIENT » — retiré le 10/09/2026 à la demande de
   * l'utilisateur, après validation de la maquette qui en portait un.
   *
   * La recherche libre de la barre trouve déjà un client en tapant son nom
   * (`filtrer()` balaie treize champs, dont `client`). Ce qui se perd, et
   * qu'il faut savoir : elle n'en trouve QU'UN. Le critère permettait
   * « COPAL ou VASSEUR ». Le rétablir tient en une entrée ici.
   */
  {
    cle: 'statut',
    nom: 'Statut',
    valeursDe: (o) => [o.statut],
    choix: STATUTS.map((s) => ({ valeur: s.cle, texte: s.nom })),
  },
  {
    cle: 'centre',
    nom: 'Activité',
    valeursDe: (o) => [o.centre_id],
    choix: null,
  },
  {
    cle: 'traite',
    nom: 'Traité par',
    // `affretement_id` est le seul témoin : `affrete_par` peut être nul sur un
    // affrètement dont le transporteur n'est pas encore choisi.
    valeursDe: (o) => [o.affretement_id === null ? 'propre' : 'affrete'],
    choix: [
      { valeur: 'propre', texte: 'En propre' },
      { valeur: 'affrete', texte: 'Affrété' },
    ],
  },
  {
    cle: 'facturation',
    nom: 'Facturation',
    /**
     * TROIS ÉTATS QUI S'EXCLUENT, et ils se DÉDUISENT — aucune colonne ne les
     * porte. `facture` est vrai dès qu'une ligne de facture porte l'ordre ;
     * `envoye_facturation_le` est le geste de l'exploitation. Un ordre facturé
     * est forcément un ordre envoyé, d'où l'ordre des tests.
     */
    valeursDe: (o) => [
      o.facture ? 'facture' : o.envoye_facturation_le !== null ? 'envoye' : 'a_envoyer',
    ],
    choix: [
      { valeur: 'a_envoyer', texte: 'À envoyer' },
      { valeur: 'envoye', texte: 'En facturation' },
      { valeur: 'facture', texte: 'Facturé' },
    ],
  },
  {
    cle: 'litige',
    nom: 'Litige',
    valeursDe: (o) => [o.litiges_ouverts > 0 ? 'avec' : 'sans'],
    choix: [
      { valeur: 'avec', texte: 'Avec litige ouvert' },
      { valeur: 'sans', texte: 'Sans litige' },
    ],
  },
];

/**
 * L'ÉTAT DES FILTRES.
 *
 * `coches` porte les critères à cases ; la vente et l'axe sont à part parce
 * qu'ils ne se cochent pas — l'un est un intervalle, l'autre deux textes.
 */
export interface Filtres {
  readonly coches: Readonly<Record<string, readonly string[]>>;
  readonly venteMin: string;
  readonly venteMax: string;
  readonly zoneDepart: string;
  readonly zoneArrivee: string;
}

/**
 * AUCUN FILTRE — l'état d'arrivée sur l'écran.
 *
 * Règle posée le 05/09 et tenue ici : « lorsque l'on arrive sur un menu aucun
 * filtre ne doit être appliqué de base ».
 */
export const AUCUN_FILTRE: Filtres = {
  coches: {},
  venteMin: '',
  venteMax: '',
  zoneDepart: '',
  zoneArrivee: '',
};

/** Les valeurs cochées d'un critère. Jamais `undefined` à l'appel. */
export function cochesDe(filtres: Filtres, cle: string): readonly string[] {
  return filtres.coches[cle] ?? [];
}

/** Coche ou décoche une valeur, et rend un nouvel état. */
export function basculer(filtres: Filtres, cle: string, valeur: string): Filtres {
  const avant = cochesDe(filtres, cle);
  const apres = avant.includes(valeur)
    ? avant.filter((v) => v !== valeur)
    : [...avant, valeur];
  const coches = { ...filtres.coches };
  // On RETIRE la clé quand elle se vide, au lieu de laisser un tableau vide :
  // c'est ce qui fait que `compteFiltres` et les étiquettes disent la vérité.
  if (apres.length === 0) delete coches[cle];
  else coches[cle] = apres;
  return { ...filtres, coches };
}

/** Combien de critères sont posés — le nombre que porte le bouton « Filtrer ». */
export function compteFiltres(filtres: Filtres): number {
  let compte = Object.values(filtres.coches).filter((v) => v.length > 0).length;
  if (filtres.venteMin.trim() !== '' || filtres.venteMax.trim() !== '') compte += 1;
  if (filtres.zoneDepart.trim() !== '' || filtres.zoneArrivee.trim() !== '') compte += 1;
  return compte;
}

/** Une zone se compare sans casse ni espace : « fr45 » trouve « FR45 ». */
function memeZone(zone: string | null, saisie: string): boolean {
  const cherche = saisie.trim().toUpperCase();
  if (cherche === '') return true;
  return (zone ?? '').toUpperCase().startsWith(cherche);
}

/**
 * APPLIQUE LES FILTRES à la liste déjà chargée.
 *
 * Un critère sans case cochée NE FILTRE PAS — sinon poser un critère puis tout
 * décocher viderait le tableau au lieu de le rendre.
 */
export function appliquerFiltres(
  ordres: readonly OrdreTransport[],
  filtres: Filtres,
): readonly OrdreTransport[] {
  const min = nombreOuNul(filtres.venteMin);
  const max = nombreOuNul(filtres.venteMax);
  const parCle = new Map(CRITERES_FILTRE.map((c) => [c.cle, c]));

  return ordres.filter((o) => {
    for (const [cle, valeurs] of Object.entries(filtres.coches)) {
      if (valeurs.length === 0) continue;
      const critere = parCle.get(cle);
      if (critere === undefined) continue; // clé inconnue : on n'invente pas de refus
      const siennes = critere.valeursDe(o);
      if (!siennes.some((v) => valeurs.includes(v))) return false;
    }

    if (min !== null || max !== null) {
      const vente = nombreOuNul(o.prix_vente);
      // SANS PRIX, ON SORT dès qu'une borne est posée : « au-dessus de 1 000 »
      // ne peut pas vouloir dire « et aussi ceux dont on ignore le prix ».
      if (vente === null) return false;
      if (min !== null && vente < min) return false;
      if (max !== null && vente > max) return false;
    }

    if (!memeZone(o.zone_depart, filtres.zoneDepart)) return false;
    if (!memeZone(o.zone_arrivee, filtres.zoneArrivee)) return false;

    return true;
  });
}

/**
 * LES ÉTIQUETTES à poser sous la barre : une par critère posé, avec ses
 * valeurs séparées par « ou ».
 *
 * `choixDe` rend les cases offertes pour un critère — l'écran les connaît, ce
 * fichier non : les clients et les activités se déduisent de la liste chargée.
 */
export function etiquettesDeFiltre(
  filtres: Filtres,
  choixDe: (cle: string) => readonly { readonly valeur: string; readonly texte: string }[],
): readonly { readonly cle: string; readonly nom: string; readonly valeurs: string }[] {
  const etiquettes: { cle: string; nom: string; valeurs: string }[] = [];

  for (const critere of CRITERES_FILTRE) {
    const coches = cochesDe(filtres, critere.cle);
    if (coches.length === 0) continue;
    const choix = choixDe(critere.cle);
    const dits = coches.map(
      (v) => choix.find((c) => c.valeur === v)?.texte ?? v,
    );
    etiquettes.push({ cle: critere.cle, nom: critere.nom, valeurs: dits.join(' ou ') });
  }

  const min = filtres.venteMin.trim();
  const max = filtres.venteMax.trim();
  if (min !== '' || max !== '') {
    const dit =
      min !== '' && max !== ''
        ? `de ${min} à ${max}`
        : min !== ''
          ? `à partir de ${min}`
          : `jusqu’à ${max}`;
    etiquettes.push({ cle: 'vente', nom: 'Vente HT', valeurs: dit });
  }

  const dep = filtres.zoneDepart.trim().toUpperCase();
  const arr = filtres.zoneArrivee.trim().toUpperCase();
  if (dep !== '' || arr !== '') {
    etiquettes.push({
      cle: 'axe',
      nom: 'Axe',
      valeurs: `${dep === '' ? '—' : dep} → ${arr === '' ? '—' : arr}`,
    });
  }

  return etiquettes;
}

/** Retire un critère entier, depuis son étiquette. */
export function oterFiltre(filtres: Filtres, cle: string): Filtres {
  if (cle === 'vente') return { ...filtres, venteMin: '', venteMax: '' };
  if (cle === 'axe') return { ...filtres, zoneDepart: '', zoneArrivee: '' };
  const coches = { ...filtres.coches };
  delete coches[cle];
  return { ...filtres, coches };
}

/**
 * LES CHOIX DÉDUITS DE LA LISTE : les activités présentes, rangées par nom.
 * On n'offre pas de case pour une activité dont aucun ordre n'est à l'écran —
 * elle ne rendrait jamais rien.
 */
export function choixDeduits(
  ordres: readonly OrdreTransport[],
  cle: string,
): readonly { readonly valeur: string; readonly texte: string }[] {
  const vus = new Map<string, string>();
  for (const o of ordres) {
    // `centre` est le seul critère déduit depuis que « Client » a été retiré.
    // La branche `client` a été enlevée avec lui : du code que personne
    // n'appelle est du code que personne ne corrige.
    if (cle === 'centre') vus.set(o.centre_id, o.centre);
  }
  return [...vus.entries()]
    // UNE CASE SANS LIBELLÉ NE SE COCHE PAS EN CONSCIENCE. Le modèle dit que
    // le centre et le client ont toujours un nom ; une fiche ancienne ou une
    // reprise de données peut démentir le modèle, et l'écran afficherait alors
    // une case muette. Constaté À LA CAPTURE le 10/09/2026 — sur un jeu
    // d'essai, mais le rendu était le même que celui qu'aurait donné une vraie
    // donnée creuse.
    .filter(([, texte]) => (texte ?? '').trim() !== '')
    .map(([valeur, texte]) => ({ valeur, texte }))
    .sort((a, b) => a.texte.localeCompare(b.texte, 'fr'));
}

/**
 * LE DÉCOMPTE QUAND UN FILTRE EST POSÉ : « 3 ordres de transport sur 6 ».
 *
 * Le « sur 6 » n'est pas décoratif. Sans lui, un filtre oublié fait croire que
 * des ordres ont disparu — c'est exactement ce qui s'était passé avec le
 * planning début septembre, et ça a coûté une demi-journée de recherche.
 */
export function decompteFiltre(visibles: number, total: number): string | null {
  return visibles === total ? null : `sur ${total}`;
}

/* `decompteFiltre` reste pour dire S'IL Y A quelque chose d'écarté ; c'est
   `decompte(visibles, total)` qui l'ÉCRIT, au bon endroit de la phrase. */

// ---------------------------------------------------------------------------
// Ce que la liste NE MONTRE PAS
// ---------------------------------------------------------------------------

/**
 * L'API rend 500 ordres au plus (`ordres_transport.ts`, `const MAXIMUM = 500`).
 * Le plafond a toujours existé ; ce qui manquait, c'était de le DIRE.
 *
 * Défaut constaté le 26/10/2026 : « 500 ordres de transport » se lisait « 500
 * au total », et un ordre plus ancien était introuvable — la barre de recherche
 * ne filtrant que ce qui est déjà chargé. Rien à l'écran ne distinguait « cet
 * ordre n'existe pas » de « cet ordre n'est pas dans les 500 chargés ».
 *
 * Trois états, et trois seulement :
 *   `rien`     — tout ce que les filtres trouvent est à l'écran. Le cas
 *                ordinaire, et l'écran se tait.
 *   `plafond`  — la coupe a mordu. L'écran dit combien il en cache.
 *   `etendue`  — la liste affichée ne vient plus de la fenêtre de dates mais
 *                d'une question posée à toute la base. L'écran doit le dire,
 *                sinon on croirait que la fenêtre s'est élargie toute seule.
 */
export type CeQueLaListeCache =
  | { readonly nom: 'rien' }
  | {
      readonly nom: 'plafond';
      readonly affiches: number;
      readonly trouves: number;
      /** Le terme de la barre de recherche, débarrassé de ses espaces. */
      readonly terme: string;
    }
  | {
      readonly nom: 'etendue';
      readonly terme: string;
      readonly affiches: number;
      readonly trouves: number;
    };

export function ceQueLaListeCache(etat: {
  readonly etendue: string | null;
  readonly recherche: string;
  readonly affiches: number;
  readonly trouves: number;
}): CeQueLaListeCache {
  // LA RECHERCHE ÉTENDUE PASSE DEVANT : quand elle est en cours, ce qui compte
  // n'est plus le plafond mais le fait que la liste répond à une autre
  // question. Son propre plafond est dit DANS ce cas-là, pas à sa place.
  if (etat.etendue !== null) {
    return {
      nom: 'etendue',
      terme: etat.etendue,
      affiches: etat.affiches,
      trouves: etat.trouves,
    };
  }
  if (etat.trouves > etat.affiches) {
    return {
      nom: 'plafond',
      affiches: etat.affiches,
      trouves: etat.trouves,
      terme: etat.recherche.trim(),
    };
  }
  return { nom: 'rien' };
}

/**
 * CHERCHER DANS TOUTE LA BASE A-T-IL QUELQUE CHOSE À TROUVER ?
 *
 * Le bouton ne doit paraître que s'il y a vraiment des ordres hors de l'écran :
 * soit le plafond a mordu, soit la fenêtre de dates en écarte. Si toute la base
 * est déjà affichée, « aucun ordre ne correspond » veut dire aucun, et proposer
 * d'aller voir ailleurs serait une fausse piste.
 *
 * PENDANT UNE RECHERCHE ÉTENDUE, non : on y est déjà.
 */
export function chercherPlusLoinSert(etat: {
  readonly etendue: string | null;
  readonly affiches: number;
  readonly trouves: number;
  readonly horsPeriode: number;
}): boolean {
  if (etat.etendue !== null) return false;
  return etat.trouves > etat.affiches || etat.horsPeriode > 0;
}
